import { withoutUnsetParams } from "@/app/shared/services/helper/request-helper";
import { DiagnosticErrorRequest, FindUser, InviteUser, RegisterUser, UsedWebappVersionRequest } from "@/app/shared/services/volateq-api/api-requests/user-requests";
import { TokenResult } from "@/app/shared/services/volateq-api/api-schemas/auth-schema";
import { CustomerSchema, OnlyOwnCustomerSchema, WalletCreditsSchema, WalletOrdersInfoSchema, WhiteLabelColors } from "@/app/shared/services/volateq-api/api-schemas/customer-schemas";
import { SimpleUserRoleSchema, SimpleUserSchema, UserAuthMethod, UserSchema } from "@/app/shared/services/volateq-api/api-schemas/user-schemas";
import { HttpClientBase } from "@/app/shared/services/volateq-api/http-client-base";
import { apiBaseUrl, baseUrl, tileServerBaseUrl } from "@/environment/environment";
import { AssignAnalysisFilesFlightRouteByNameRequest, AssignAnalysisFilesFlightRouteRequest, AssignAnalysisRoleRequest, MoveAnalysisFilesRequest, NewEmptyAnalysisRequest, UpdateAnalysisInvoiceRequest, UpdateAnalysisRequest, UpdateAnalysisStateRequest } from "./api-requests/analysis-requests";
import type { AnalysisDataCheckSchema, DataCheckRunningTasksSchema } from "./api-schemas/analysis-data-check-schema";
import type { AnalysisTimingSchema, UpdateAnalysisDeadlineRequest } from "./api-schemas/analysis-timing-schema";
import type { GlacierRestoreResultSchema, GlacierRestoreStatusSchema } from "./api-schemas/glacier-restore-status-schema";
import type { MissionCoverageFileCountsSchema } from "./api-schemas/analysis-flight-mission-cover-schema";
import { ClonePlantRequest, CreateDigitTwinRequest, CreatePlantRequest, FieldgeoDtIdsValidationRequest, UpdatePlantFlightAreasAndTakeOffPositionsRequest, UpdatePlantRequest } from "./api-requests/plant-requests";
import { UpdatePlantAreasRequest } from "./api-requests/plant-area-requests";
import { PlantAreaSchema } from "./api-schemas/plant-area-schema";
import { SuggestFlightAreaPolygonRequest } from "./api-requests/flight-area-suggestion-requests";
import { SuggestedFlightAreaPolygonsSchema } from "./api-schemas/flight-area-suggestion-schema";
import { AnalysisFileFlightRouteTargetSchema, AnalysisFileInfoSchema, AnalysisFileInfosResultSchema, AnalysisFilesSyncResultSchema, AnalysisForViewSchema, AnalysisNameCustomerPlantSchema, AnalysisWithInvoiceSchema, SimpleAnalysisSchema } from "./api-schemas/analysis-schema";
import { CustomerWhatsAppLinkSchema, WhatsAppAccountStatusSchema, WhatsAppChatSchema, WhatsAppMessagesSchema } from "./api-schemas/whatsapp-schema";
import { AnalysisNoteSchema, AnalysisNoteSubscriptionStatus, NoteUnreadStatusByAnalysis } from "./api-schemas/analysis-note-schema";
import { NotificationSchema } from "./api-schemas/notification-schema";
import { PlantSchema, PlantTableSchema, SimplePlantForOrdersSchema, SimplePlantSchema, SimplePlantWithLastActivitySchema } from "./api-schemas/plant-schema";
import { AnalysisResultChangeHistorySchema, AnalysisResultDetailedSchema, AnalysisResultHistogramBinSchema, AnalysisResultHistorySchema } from "./api-schemas/analysis-result-schema";
import type { ChangedBy } from "./api-schemas/analysis-result-schema";
import type { AIPerformanceSchema } from "./api-schemas/ai-performance-schema";
import type { RelabelingDurationSchema } from "./api-schemas/relabeling-duration-schema";
import { CsvColumn, TableFilterRequest, TableRequest } from "./api-requests/common/table-requests";
import { AnalysisResultFileSchema } from "./api-schemas/analysis-result-file-schema";
import { AnalysisResultOmoFileSchema } from "./api-schemas/analysis-result-omo-file-schema";
import { ApiComponent } from "./api-components/api-components";
import { ApiKeyFigure } from "./api-key-figures";
import { GeoVisualCspPtcQuery, GeoVisualPvQuery, GeoVisualQuery } from "./api-requests/geo-visual-query-requests";
import { TableResultSchema } from "./api-schemas/table-result-schema";
import { UserVolaflyLogSchema } from "./api-schemas/user-volafly-log-schema";
import { TaskSchema } from "./api-schemas/task-schema";
import { ApiStates } from "./api-states";
import { AssignCustomerF2cUserRequest, CreateCustomerRequest, UpdateCustomerRequest, UpdateOnlyOwnCustomerRequest } from "./api-requests/customer-requests";
import { TechnologySchema } from "./api-schemas/technology-schema";
import {
  AnalysisConfigYamlAvailabilityEntry,
  AnalysisConfigYamlResponse,
  AnalysisMonitoringEntry,
  OpsArtifactFile,
  OpsArtifactFileRef,
  OpsArtifactSlot,
  OpsRasterInfo,
} from "./api-schemas/analysis-monitoring";
import type { RunningEvaluationSchema } from "./api-schemas/running-evaluation-schema";
import { BatchJobLogsSchema, MetashapeLicenseUsageSchema, QFlyServerSchema, RunpodAvailabilitySchema, RunpodLogsSchema, RunpodNetworkVolumeSchema, RunpodServerSchema } from "./api-schemas/server-schemas";
import { CreateRunpodServerRequest, LaunchTaskForAnalysisRequest, QFlyServerUpdateRequest, RunpodAvailabilityRequest, RunpodServerUpdateRequest } from "./api-requests/server-requests";
import { ProductPackageSchema, ProductPackageWithKeyFiguresSchema } from "./api-schemas/product-package";
import { PvFlightParamsSchema } from "./api-schemas/pv-flight-params-schema";
import { CreateOrderRequest, UpdateOrderRequest } from "./api-requests/order-requests";
import { OrderPPKeyFiguresDisabledSchema, OrderProductPackageSchema, OrderSchema, OrderWithZohoSchema } from "./api-schemas/order-schema";
import { MathHelper } from "../helper/math-helper";
import { OrderHelper } from "../helper/order-helper";
import type { OrderPPMultiselectOption } from "../helper/order-helper";
import { CompleteUploadFilesSchema, SecuredFilename, Upload, UploadFileChunkSchema } from "./api-schemas/upload-schemas";
import { CompleteUploadFilesRequest, CreateAnalysisTmpUploadRequest, CreateAnalysisUploadRequest, CreateUploadPartUrlRequest, FinishUploadRequest, StartUploadFilesRequest } from "./api-requests/upload-requests";
import { HistogramRequest, ModfifyAnalysisResultValueRequest } from "./api-requests/analysis-result-requests";
import type { ChangedAnalysisResultsSchema } from "./api-schemas/changed-analysis-results-schema";
import type { HotJunctionBoxDeltaTemperaturesSchema } from "./api-schemas/hot-junction-box-delta-temperatures-schema";
import { KeyFigureSchema } from "./api-schemas/key-figure-schema";
import type { ComponentSchema } from "./api-schemas/component-schema";
import type { CoreMetadataPerformanceIndicatorSchema } from "./api-schemas/core-metadata-schema";
import type { UpdateComponentRequest, UpdatePerformanceIndicatorRequest } from "./api-requests/core-metadata-requests";
import { CreateProductPackageRequest, ProductPackagesWithKeyFiguresRequest, UpdateProductPackageRequest } from "./api-requests/product-package-requests";
import { UpdatePvFlightParamsRequest } from "./api-requests/pv-flight-params-requests";
import { UpdatePlantStatusRequest } from "./api-requests/plant-status-requests";
import { PlantStatusSchema } from "./api-schemas/plant-status-schema";
import { CreateDroneModelRequest, UpdateDroneModelRequest, CreateDroneRequest, UpdateDroneRequest, DronesRequest } from "./api-requests/drone-requests";
import { DetailedDroneModelSchema, DetailedDroneSchema, DroneSchema, SimpleDroneSchema } from "./api-schemas/drone-schemas";
import { FlightCampaignSchema } from "./api-schemas/flight-campaign-schema";
import type { AnalysisFileFlightCampaignsSchema } from "./api-schemas/analysis-file-flight-campaigns-schema";
import { CreateFlightCampaignRequest, DuplicateFlightCampaignRequest, UpdateFlightCampaignRequest, UpdatePlantOperationActionStartTimeRequest } from "./api-requests/flight-campaign-requests";
import { FlightRouteMissionSchema, FlightRouteSchema } from "./api-schemas/flight-route-schema";
import { PvEditFlightCampaignResultSchema, PvFlightRouteCalculationResultSchema } from "./api-schemas/pv-flight-route-calculation-schema";
import { EditPvFlightCampaignRequest, ReschedulePvFlightCampaignRequest } from "./api-requests/pv-flight-route-calculation-requests";
import { PlantOperationActionSchema } from "./api-schemas/plant-operation-action-schema";
import { CustomComponentPropertySchema } from "./api-schemas/custom-component-property-schema";
import { CustomComponentPropertyRequest } from "./api-requests/custom-component-property-request";
import { DeleteObservationComponentsRequest, ObservFilterValue, ObservationComponentsRequest, ObservationFilterRequest, ObservationsRequest, SummerizedObservationRequest } from "./api-requests/observation-requests";
import { CreatedObservationSchema, ObservationColumn, ObservationSchema, ReferenceMeasurementsSchema, SummerizedDates } from "./api-schemas/observation-schema";
import { ObservationRequest } from "./api-requests/observation-requests";
import { DisablePiFieldsRequest, EnablePiFieldsRequest } from "./api-requests/enabled-pi-field-requests";
import { EnabledPiFieldSchema } from "./api-schemas/enabled-pi-field-schema";
import { IVolateqOfflineApi } from "./offline/types";
import { OfflineDB, OfflineDBInstance } from "./offline/offline-db";
import { FeatureLike } from "ol/Feature";
import { store } from "../../stores/store";
import { RoleSchema } from "./api-schemas/role-schema";
import { PermissionsMatrixSchema } from "./api-schemas/permission-schema";
import { InvoiceStateSchema } from "./api-schemas/invoice-state-schema";
import { CreateReportRequest } from "./api-requests/report-request";
import {
  ImportFilesFromFlighthub2Request,
  ExportFlightCampaignFlighthub2Request,
  StartFlighthub2LiveStreamRequest,
  UpdateFlighthub2LiveStreamJoinStateRequest,
} from "./api-requests/flighthub2-requests";
import { ReportSchema, ReportModuleAnomalyCountsSchema } from "./api-schemas/report-schema";
import {
  Flighthub2DockDeviceInfoSchema,
  Flighthub2DockSummarySchema,
  Flighthub2FlightRouteSchema,
  Flighthub2FolderSchema,
  Flighthub2LiveStreamSchema,
  Flighthub2OrganizationSchema,
  Flighthub2ProjectSchema,
  Flighthub2TaskPlanSchema,
} from "./api-schemas/flighthub2-schema";
import { AxiosProgressEvent } from "axios";
import { PerformanceIndicatorSchema } from "./api-schemas/performance-indicator";
import { ApiPI } from "./api-performance-indicator";
import { ApiTechnology } from "./api-technologies";
import { UnleashFolderSchema } from "./api-schemas/unleash-schema";
import { AnalysisResultReleaseNotesSchema } from "./api-schemas/analysis-result-release-notes-schema";
import { AnalysisResultReleaseMailSchema } from "./api-schemas/analysis-result-release-mail-schema";
import { ImportFilesFromUnleashRequest } from "./api-requests/unleash-requests";
import { PlantBlueprintPdfFileSchema, PlantBlueprintSchema } from "./api-schemas/plant-blueprint-schema";
import { CreatePlantPdfBlueprintRequest, RenamePcsRequest, UpdatePlantPdfBlueprintRequest, ValidateKmlNomenclatureImportRequest } from "./api-requests/plant-blueprint-request";
import { Locale } from "@/main";
import { DashboardSchema, WidgetWithParamsSchema } from "./api-schemas/dashboard-schema";
import type { CompareAnalysisComparison, CompareAnalysisData, CompareAnalysisPiHistoryEntry } from "./api-schemas/dashboard-compare-analysis-schema";
import { UpdateWidgetRequest } from "./api-requests/dashboard-requests";
import { EvaluationBatchSchema } from "./api-schemas/evaluation-batch-schema";
import { EvalBatchFilterRequest } from "./api-requests/evaluation-batch-requests";
import { AnalysisFilesSearchRequest } from "./api-requests/analysis-file-requests";
import { MoveFlightRouteRequest, UpdateFlightRouteEvalBatchRequest, UpdateFlightRouteStartTimeRequest } from "./api-requests/flight-route-requests";
import { FieldgeoValidationResult } from "./api-schemas/fieldgeometry-schema";
import type {
  AreaTemperaturesSchema,
  OmoUrlToken,
  PointTemperatureSchema,
  TemperatureInfoSchema,
  TemperatureRange,
} from "./api-schemas/orthomosaic-schema";
import type { Model3dUrlsSchema } from "./api-schemas/model-3d-schema";
import { UpdateFieldgeometrySeverityLimitsRequest } from "./api-requests/fieldgeometry-requests";
import { DxfValidationResult } from "./api-schemas/dxf-validation-result-schema";
import type { ApiKeysSchema, CreatedApiKeySchema } from "./api-schemas/api-key-schema";
import type { CreateApiKeyRequest, UpdateApiKeyCorsOriginRequest } from "./api-requests/api-key-requests";
import type { PublicApiDocSchema } from "./api-schemas/public-api-doc-schema";
import type { OpsReassignmentResultSchema, OpsTeamsSchema } from "./api-schemas/ops-team-schema";
import { EvalBatchSelectionHelper } from "../helper/eval-batch-pills-helper";

export class VolateqAPI extends HttpClientBase implements IVolateqOfflineApi {
  //
  // LOGIN, LOGOUT, REGISTER, CONFIRM, ... - All "Auth" actions 
  //

  public async login(email: string, password: string): Promise<{ confirmation_key: string, auth_method: UserAuthMethod, reset_mfa_user_id: string | null }> {
    return await this.post(
      "/auth/login",
      {},
      {
        auth: {
          username: email,
          password: password,
        },
      }
    );
  }

  public async isLoggedIn(): Promise<boolean> {
    if (store.auth.isAuthenticated) {
      try {
        const pong: { pong: true } = await this.get("/auth/ping");

        if (pong.pong) {
          return true;
        }
      } catch {
        return false;
      }
    }

    return false;
  }

  public async confirmMailLogin(confirmationKey: string, securityCode: string): Promise<void> {
    const tokenResult: TokenResult = await this.post(`/confirm-email-login/${confirmationKey}`, {
      security_code: securityCode,
    });

    store.auth.updateToken({
      token: tokenResult.token,
      refreshToken: tokenResult.refresh_token,
      permissions: tokenResult.permissions,
      customer: tokenResult.customer,
      whiteLabelingVersion: tokenResult.white_labeling_version,
      ...(tokenResult.is_volateq_admin ? { isAdmin: tokenResult.is_volateq_admin } : {}),
    });
  }

  public async confirmTotpLogin(confirmationKey: string, securityCode: string): Promise<void> {
    const tokenResult: TokenResult = await this.post(`/confirm-totp-login/${confirmationKey}`, {
      security_code: securityCode,
    });

    store.auth.updateToken({
      token: tokenResult.token,
      refreshToken: tokenResult.refresh_token,
      permissions: tokenResult.permissions,
      customer: tokenResult.customer,
      whiteLabelingVersion: tokenResult.white_labeling_version,
      ...(tokenResult.is_volateq_admin ? { isAdmin: tokenResult.is_volateq_admin } : {}),
    });
  }

  public async logout(): Promise<void> {
    await this.post("/auth/logout");

    store.auth.updateToken({ token: "", permissions: [], customer: undefined, whiteLabelingVersion: undefined, refreshToken: undefined });

    // Offline plant data and the DT cache contain customer data and must not
    // outlive an explicit logout. Only done here — not on session expiry in
    // http-client-base — so an expired token in the field does not wipe data
    // needed for offline usage.
    store.offline.goOnline();
    await OfflineDB.deleteDatabase(OfflineDBInstance.VOLATEQ_OFFLINE_DB);
    await OfflineDB.deleteDatabase(OfflineDBInstance.DT);
  }

  /**
   * @returns true if offline plant data is still stored in the browser.
   */
  public hasOfflineData(): Promise<boolean> {
    return OfflineDB.exists(OfflineDBInstance.VOLATEQ_OFFLINE_DB);
  }

  public async getInvitedUser(confirmKey: string): Promise<UserSchema> {
    return this.get(`/register/${confirmKey}`);
  }

  public async registerUser(confirmKey: string, user: RegisterUser): Promise<{ confirmation_key?: string }> {
    return await this.post(`/register/${confirmKey}`, user);
  }

  public forgotPassword(email: string): Promise<void> {
    return this.post(`/forgot-password`, { email });
  }

  public confirmPasswordReset(confirmationKey: string, email: string): Promise<void> {
    return this.get(`/reset-password/${confirmationKey}`, { email });
  }

  public resetPassword(confirmationKey: string, email: string, new_password: string, new_password_repeat: string): Promise<void> {
    return this.post(`/reset-password/${confirmationKey}`, { email, new_password, new_password_repeat });
  }

  public async resendSecurityCode(confirmationKey: string): Promise<void> {
    await this.post(`/confirm-email-login-resend/${confirmationKey}`, {});
  }

  public async getTotpSecrets(confirmationKey: string): Promise<{ url: string, secret: string }> {
    return await this.get(`/totp-secrets/${confirmationKey}`);
  }

  public async verifyTotpCode(confirmationKey: string, securityCode: string): Promise<void> {
    await this.post(`/totp-secrets/${confirmationKey}`, { security_code: securityCode });
  }

  public async cancelSetupTotpMfa(confirmationKey: string): Promise<void> {
    await this.delete(`/totp-secrets/${confirmationKey}`);
  }

  //
  // User
  //

  public async getMe(): Promise<UserSchema> {
    return await this.get("/auth/user/me");
  }

  public async getUsers(filter: FindUser): Promise<TableResultSchema<UserSchema> & { user_limit?: { used: number; max: number } | null }> {
    return this.get("/auth/users", filter);
  }

  public async getVolateqUsers(): Promise<SimpleUserRoleSchema[]> {
    return this.get("/auth/volateq-users");
  }

  public getOpsTeams(): Promise<OpsTeamsSchema> {
    return this.get("/auth/ops-teams");
  }

  public setMyOpsTechnologies(technologyIds: ApiTechnology[]): Promise<OpsReassignmentResultSchema> {
    return this.post("/auth/ops-teams/me", { technology_ids: technologyIds });
  }

  public removeOpsTeamMember(technologyId: ApiTechnology, userId: string): Promise<OpsReassignmentResultSchema> {
    return this.delete(`/auth/ops-teams/${technologyId}/member/${userId}`);
  }

  public async inviteUser(user: InviteUser): Promise<string> {
    const confirmKey = (await this.post("/auth/user", user)).confirmation_key;

    return `${baseUrl}/register/${confirmKey}`;
  }

  public async assignPlantsToUser(userId: string, plantIds: string[]): Promise<void> {
    await this.post(`/auth/user/${userId}/assign-plants`, { plant_ids: plantIds });
  }

  public async changeUserRole(userId: string, roleId: number, securityCode?: string): Promise<void> {
    await this.post(`/auth/user/${userId}/change-role`, { role_id: roleId, security_code: securityCode });
  }

  public async deleteUser(userId): Promise<void> {
    await this.delete(`/auth/user/${userId}/delete`);
  }

  public async generateDownloadUrl(downloadUrl: string): Promise<string> {
    const concatURLTokenSign = downloadUrl.includes("?") ? "&" : "?";

    const urlTokenResponse: { url_token: string } = await this.post(
      `/auth/user/generate-url-token`,
      { url: encodeURIComponent(downloadUrl) }
    );

    return `${downloadUrl}${concatURLTokenSign}url_token=${encodeURIComponent(urlTokenResponse.url_token)}`;
  }

  public resendUserInvitation(userId: string): Promise<void> {
    return this.post(`/auth/user/${userId}/resend-invitation`);
  }

  public unLockUser(userId: string, lock: boolean): Promise<void> {
    return this.post(`/auth/user/${userId}/un-lock`, { lock });
  }

  public resetUserMfa(userId: string): Promise<void> {
    return this.post(`/auth/user/${userId}/reset-mfa`);
  }

  public async resetMyMfa(authMethod: UserAuthMethod, confirmationKey: string, userId: string): Promise<{ confirmation_key: string | null }> {
    return this.post(`/reset-my-mfa`, { auth_method: authMethod, confirmation_key: confirmationKey, user_id: userId });
  }

  public async switchCustomer(
    toCustomerId: string | undefined,
    showAllKeyFigures: boolean,
    hideAdminFeatures: boolean,
  ): Promise<TokenResult> {
    return this.post(`/auth/user/switch-customer`, { 
      customer_id: toCustomerId,
      show_all_key_figures: showAllKeyFigures,
      hide_admin_features: hideAdminFeatures,
    });
  }

  public async prepareUserChangeAuthMethod(authMethod: UserAuthMethod, password: string): Promise<{confirmation_key: string}> {
    return await this.post(`/auth/user/prepare-change-auth-method`, { auth_method: authMethod, password });
  }

  public async changeOwnNames(firstName: string, lastName: string): Promise<void> {
    await this.post(`/auth/user/me/names`, { first_name: firstName, last_name: lastName });
  }

  public async requestEmailChange(newEmail: string): Promise<void> {
    await this.post(`/auth/user/me/email`, { new_email: newEmail });
  }

  public async confirmEmailChange(confirmationKey: string): Promise<void> {
    await this.post(`/auth/user/me/email-confirm`, { confirmation_key: confirmationKey });
  }

  public async changeUserCustomer(userId: string, customerId: string, plantIds: string[], securityCode: string): Promise<void> {
    await this.post(`/auth/user/${userId}/change-customer`, {
      customer_id: customerId,
      plant_ids: plantIds,
      security_code: securityCode,
    });
  }

  public async changeUserAuthMethod(authMethod: UserAuthMethod, confirmationKey: string, securityCode: string, newSecurityCode: string): Promise<void> {
    await this.post(`/auth/user/change-auth-method/${confirmationKey}`, { 
      auth_method: authMethod,
      security_code: securityCode,
      new_security_code: newSecurityCode,
    });
  }

  public async updateWebAppVersion(usedWebappVersionRequest: UsedWebappVersionRequest) {
    await this.post(`/auth/user/update-app-version`, usedWebappVersionRequest );
  }

  public async getRoles(): Promise<RoleSchema[]> {
    return this.get(`/auth/user/roles`);
  }

  public async getPermissions(): Promise<PermissionsMatrixSchema> {
    return this.get(`/auth/user/permissions`);
  }

  public async sendDiagnostics(diagnosticErros: DiagnosticErrorRequest[]): Promise<void> {
    await this.post(`/auth/user/send-diagnostics`, { errors: diagnosticErros });
  }

  //
  // Public API keys
  //

  public async getApiKeys(): Promise<ApiKeysSchema> {
    return this.get(`/auth/user/api-keys`);
  }

  public async createApiKey(createApiKeyRequest: CreateApiKeyRequest): Promise<CreatedApiKeySchema> {
    return this.post(`/auth/user/api-key`, createApiKeyRequest);
  }

  public async updateApiKeyCorsOrigin(
    apiKeyId: string,
    updateApiKeyCorsOriginRequest: UpdateApiKeyCorsOriginRequest,
  ): Promise<void> {
    await this.put(`/auth/user/api-key/${apiKeyId}`, updateApiKeyCorsOriginRequest);
  }

  public async deleteApiKey(apiKeyId: string): Promise<void> {
    await this.delete(`/auth/user/api-key/${apiKeyId}`);
  }

  public async getPublicApiDoc(): Promise<PublicApiDocSchema> {
    return this.get(`/auth/public-api/docs`);
  }

  //
  // Analyis
  //

  public async createEmptyAnalysis(newEmptyAnalyis: NewEmptyAnalysisRequest): Promise<{ id: string }> {
    return this.post(`/auth/analysis/create-empty`, newEmptyAnalyis);
  }

  public async getAllAnalyses(
    queryParams?: { plant_id?: string }
  ): Promise<AnalysisWithInvoiceSchema[]> {
    return this.get(`/auth/analysis`, queryParams);
  }

  public async getAllAnalysesNameCustomerPlant(
    queryParams?: { plant_id?: string, technology_id?: number }
  ): Promise<AnalysisNameCustomerPlantSchema[]> {
    return this.get(`/auth/analysis-name-customer-plant`, queryParams);
  }

  public async getAnalyses(
    queryParams?: { plant_id?: string, technology_id?: string, customer_id?: string } & TableRequest & { limit: number },
  ): Promise<TableResultSchema<AnalysisWithInvoiceSchema>> {
    return this.get(`/auth/analysis`, queryParams);
  }

  public async getIncompleteAnalyses(queryParams?: { plant_id?: string, technology_id?: string, customer_id?: string }): Promise<AnalysisWithInvoiceSchema[]> {
    return this.get(`/auth/analyses/incomplete`, queryParams);
  }

  public getAnalysis(analysisId?: string): Promise<AnalysisWithInvoiceSchema> {
    return this.get(`/auth/analysis/${analysisId}`);
  }

  public async getAnalysesForView(plantId: string): Promise<AnalysisForViewSchema[]> {
    const analyses: AnalysisForViewSchema[] = await this.get(`/auth/analyses-for-view/${plantId}`);

    for (const analysis of analyses) {
      if (analysis.analysis_result) {
        this.replaceKeyFigureIRWithGlassTubeTemp(analysis.analysis_result);
      }
    }

    return analyses;
  }

  public async updateAnalysisState(analysisId: string, analysisState: UpdateAnalysisStateRequest): Promise<void> {
    await this.post(`/auth/analysis/${analysisId}/state`, analysisState);
  }

  public getStates(): Promise<Record<ApiStates, ApiStates[]>> {
    return this.get(`/auth/states`);
  }

  public async deleteAnalysis(analysisId: string): Promise<void> {
    await this.delete(`/auth/analysis/${analysisId}`);
  }

  public getAnalysisNotesAndHistory(analysisId: string): Promise<{ notes_and_history: string }> {
    return this.get(`/auth/analysis/${analysisId}/notes-and-history`);
  }

  public appendAnalysisNote(analysisId: string, note: string): Promise<void> {
    return this.post(`/auth/analysis/${analysisId}/notes-and-history`, { note });
  }

  public getAnalysisNotes(analysisId: string): Promise<AnalysisNoteSchema[]> {
    return this.get(`/auth/analysis/${analysisId}/notes`);
  }

  public addAnalysisNote(
    analysisId: string,
    note: string,
    mentionedUserIds: string[],
    evalBatchNum: number | null = null,
    parentNoteId: string | null = null,
  ): Promise<{ id: string }> {
    return this.post(`/auth/analysis/${analysisId}/notes`, {
      note,
      mentioned_user_ids: mentionedUserIds,
      eval_batch_num: evalBatchNum,
      parent_note_id: parentNoteId,
    });
  }

  public updateAnalysisNote(
    analysisId: string,
    noteId: string,
    note: string,
    mentionedUserIds: string[],
    evalBatchNum: number | null = null,
  ): Promise<void> {
    return this.put(`/auth/analysis/${analysisId}/notes/${noteId}`, {
      note,
      mentioned_user_ids: mentionedUserIds,
      eval_batch_num: evalBatchNum,
    });
  }

  public deleteAnalysisNote(analysisId: string, noteId: string): Promise<void> {
    return this.delete(`/auth/analysis/${analysisId}/notes/${noteId}`);
  }

  public uploadAnalysisNoteAttachments(analysisId: string, noteId: string, files: File[]): Promise<void> {
    const data: Record<string, File> = {};
    files.forEach((file, index) => {
      data[`file_${index}`] = file;
    });

    return this.postForm(`/auth/analysis/${analysisId}/notes/${noteId}/attachments`, data);
  }

  public deleteAnalysisNoteAttachment(analysisId: string, noteId: string, attachmentId: string): Promise<void> {
    return this.delete(`/auth/analysis/${analysisId}/notes/${noteId}/attachments/${attachmentId}`);
  }

  public markAnalysisNotesRead(analysisId: string): Promise<void> {
    return this.post(`/auth/analysis/${analysisId}/notes/read`, {});
  }

  public async addAnalysisNoteReaction(analysisId: string, noteId: string, emoji: string): Promise<void> {
    await this.post(`/auth/analysis/${analysisId}/notes/${noteId}/reactions`, { emoji });
  }

  public async removeAnalysisNoteReaction(analysisId: string, noteId: string, emoji: string): Promise<void> {
    await this.delete(`/auth/analysis/${analysisId}/notes/${noteId}/reactions`, { data: { emoji } });
  }

  public getAnalysisNotesUnreadStatus(analysisIds: string[]): Promise<NoteUnreadStatusByAnalysis> {
    return this.post(`/auth/analysis/notes/unread-status`, { analysis_ids: analysisIds });
  }

  public getAnalysisWhatsAppMessages(analysisId: string): Promise<WhatsAppMessagesSchema> {
    return this.get(`/auth/analysis/${analysisId}/whatsapp/messages`);
  }

  public sendAnalysisWhatsAppMessage(
    analysisId: string,
    text: string,
    attachment?: File | null,
  ): Promise<{ id: string }> {
    if (attachment) {
      return this.postForm(`/auth/analysis/${analysisId}/whatsapp/messages`, {
        text,
        attachment,
      });
    }
    return this.post(`/auth/analysis/${analysisId}/whatsapp/messages`, { text });
  }

  /** Deletes an own WhatsApp message for everyone in the linked chat. */
  public deleteAnalysisWhatsAppMessage(analysisId: string, messageId: string): Promise<void> {
    return this.delete(`/auth/analysis/${analysisId}/whatsapp/messages/${messageId}`);
  }

  public getAnalysisWhatsAppAttachment(analysisId: string, messageId: string, attachmentId: string): Promise<Blob> {
    return this.get(
      `/auth/analysis/${analysisId}/whatsapp/messages/${messageId}/attachments/${attachmentId}`,
      undefined,
      { responseType: "blob" },
    );
  }

  public getAnalysisWhatsAppAttendeePicture(analysisId: string, attendeeId: string): Promise<Blob> {
    return this.get(
      `/auth/analysis/${analysisId}/whatsapp/attendees/${attendeeId}/picture`,
      undefined,
      { responseType: "blob" },
    );
  }

  /** Links (or clears, when chatId is null) the WhatsApp chat for the analysis' whole customer_plant. */
  public linkAnalysisWhatsAppChat(analysisId: string, chatId: string | null, chatName: string | null): Promise<void> {
    return this.post(`/auth/analysis/${analysisId}/whatsapp/link`, { chat_id: chatId, chat_name: chatName });
  }

  public getWhatsAppChats(): Promise<WhatsAppChatSchema[]> {
    return this.get(`/auth/whatsapp/chats`);
  }

  public getCustomerWhatsAppLinks(customerId: string): Promise<CustomerWhatsAppLinkSchema[]> {
    return this.get(`/auth/customer/${customerId}/whatsapp/links`);
  }

  /**
   * Links (or clears, when chatId is null) the WhatsApp chat for one customer_plant.
   * A null plantId sets the all-plants fallback.
   */
  public linkCustomerWhatsAppChat(
    customerId: string,
    plantId: string | null,
    chatId: string | null,
    chatName: string | null,
  ): Promise<void> {
    return this.post(`/auth/customer/${customerId}/whatsapp/links`, {
      plant_id: plantId,
      chat_id: chatId,
      chat_name: chatName,
    });
  }

  public getWhatsAppConnectLink(): Promise<{ url: string }> {
    return this.get(`/auth/whatsapp/connect`);
  }

  public getWhatsAppAccountStatus(): Promise<WhatsAppAccountStatusSchema> {
    return this.get(`/auth/whatsapp/account`);
  }

  public getAnalysisNotesSubscription(analysisId: string): Promise<AnalysisNoteSubscriptionStatus> {
    return this.get(`/auth/analysis/${analysisId}/notes/subscription`);
  }

  public subscribeAnalysisNotes(analysisId: string): Promise<AnalysisNoteSubscriptionStatus> {
    return this.put(`/auth/analysis/${analysisId}/notes/subscription`, {});
  }

  public unsubscribeAnalysisNotes(analysisId: string): Promise<AnalysisNoteSubscriptionStatus> {
    return this.delete(`/auth/analysis/${analysisId}/notes/subscription`);
  }

  public getNotifications(
    onlyUnread = false,
    page = 0,
    limit = 50,
    onlyRead = false,
  ): Promise<NotificationSchema[]> {
    return this.get(`/auth/notifications`, { only_unread: onlyUnread, only_read: onlyRead, page, limit });
  }

  public getUnreadNotificationCount(): Promise<{ count: number }> {
    return this.get(`/auth/notifications/unread-count`);
  }

  public async markNotificationRead(notificationId: string): Promise<void> {
    await this.post(`/auth/notifications/${notificationId}/read`);
  }

  public async markNotificationUnread(notificationId: string): Promise<void> {
    await this.post(`/auth/notifications/${notificationId}/unread`);
  }

  public async markAllNotificationsRead(): Promise<void> {
    await this.post(`/auth/notifications/read-all`);
  }

  public async markAnalysisNotificationsRead(analysisId: string): Promise<void> {
    await this.post(`/auth/notifications/analysis/${analysisId}/read-all`);
  }

  // Slow endpoint (budget 30 s): trigger it from an <app-button long-loading-time>
  public async updateAnalysis(
    analysisId: string,
    updateData: UpdateAnalysisRequest,
  ): Promise<void> {
    await this.post(`/auth/analysis/${analysisId}`, updateData);
  }

  public async getAnalysisMissionCoverageFileCounts(
    analysisId: string,
  ): Promise<MissionCoverageFileCountsSchema> {
    return this.get(`/auth/analysis/${analysisId}/visual-mission-coverage/file-counts`);
  }

  public async getAnalysisTimings(analysisId: string): Promise<AnalysisTimingSchema[]> {
    return this.get(`/auth/analysis/${analysisId}/timings`);
  }

  // Slow endpoint (budget 40 s): trigger it from an <app-button long-loading-time>
  public async getAnalysisQflyfoodGlacierRestoreStatus(analysisId: string): Promise<GlacierRestoreStatusSchema> {
    return this.get(`/auth/analysis/${analysisId}/qfly-food/glacier-restore`);
  }

  // Slow endpoint (budget 40 s): trigger it from an <app-button long-loading-time>
  public async restoreAnalysisQflyfoodFromGlacier(analysisId: string): Promise<GlacierRestoreResultSchema> {
    return this.post(`/auth/analysis/${analysisId}/qfly-food/glacier-restore`);
  }

  public async updateAnalysisDeadline(
    analysisId: string,
    updateDeadlineData: UpdateAnalysisDeadlineRequest,
  ): Promise<AnalysisTimingSchema> {
    return this.post(`/auth/analysis/${analysisId}/timings`, updateDeadlineData);
  }

  public async assignAnalysisRole(
    analysisId: string,
    assignRoleData: AssignAnalysisRoleRequest,
  ): Promise<void> {
    await this.post(`/auth/analysis/${analysisId}/assign-roles`, assignRoleData);
  }

  public getAnalysisFileDownloadUrl(analysisId: string, fileName: string, forceDownload?: boolean): Promise<{ url: string }> {
    return this.get(`/auth/analysis/${analysisId}/file/${fileName}`,
      {
        ...(forceDownload !== undefined ? { force_download: (forceDownload ? "1" : "0") } : {})
      }
    );
  }

  public getAnalysisFileEmbeddedSubtitle(analysisId: string, fileName: string): Promise<{ content: string }> {
    return this.get(`/auth/analysis/${analysisId}/file/${fileName}/embedded-subtitle`);
  }

  public getAnalysisFileVideoFrame(analysisId: string, fileName: string, frame: number): Promise<Blob> {
    return this.get(
      `/auth/analysis/${analysisId}/file/${fileName}/frame`,
      { frame },
      { responseType: "blob" }
    );
  }

  public downloadMultipleAnalysisFilesUrl(analysisId: string, search: AnalysisFilesSearchRequest) {
    for (const k of Object.keys(search)) {
      if (search[k] === undefined || search[k] === null) {
        delete search[k]
      }
    }

    return this.getUrl(`${apiBaseUrl}/auth/analysis/${analysisId}/files/download`, search);
  }

  public getAnalysisFiles(analysisId: string, search: AnalysisFilesSearchRequest): Promise<AnalysisFileInfoSchema | AnalysisFileInfosResultSchema> {
    return this.post(`/auth/analysis/${analysisId}/files`, search);
  }

  /**
   * Reads the size and upload date of all files of the analysis from the storage into the
   * database. Runs at most once per analysis unless "force" is passed, which is admin only.
   * Slow endpoint (budget 120 s): trigger it from an <app-button long-loading-time>
   */
  public syncAnalysisFiles(analysisId: string, force = false): Promise<AnalysisFilesSyncResultSchema> {
    return this.post(`/auth/analysis/${analysisId}/files/sync${force ? "?force=1" : ""}`, {});
  }

  public async deleteAnalysisFiles(analysisId: string, search: AnalysisFilesSearchRequest, expectedCount: number): Promise<void> {
    await this.post(`/auth/analysis/${analysisId}/files/delete?expected_count=${expectedCount}`, search);
  }

  public async moveAnalysisFiles(analysisId: string, moveFilesRequest: MoveAnalysisFilesRequest, expectedCount: number): Promise<void> {
    await this.post(`/auth/analysis/${analysisId}/files/move?expected_count=${expectedCount}`, moveFilesRequest);
  }

  public getAnalysisFileFlightRouteTargets(analysisId: string): Promise<AnalysisFileFlightRouteTargetSchema[]> {
    return this.get(`/auth/analysis/${analysisId}/files/flight-route-targets`);
  }

  public async assignAnalysisFilesFlightRoute(
    analysisId: string,
    assignFlightRouteRequest: AssignAnalysisFilesFlightRouteRequest,
    expectedCount: number,
  ): Promise<void> {
    await this.post(`/auth/analysis/${analysisId}/files/flight-route?expected_count=${expectedCount}`, assignFlightRouteRequest);
  }

  public async assignAnalysisFilesFlightRouteByName(
    analysisId: string,
    assignFlightRouteRequest: AssignAnalysisFilesFlightRouteByNameRequest,
  ): Promise<void> {
    await this.post(`/auth/analysis/${analysisId}/files/flight-route/by-name`, assignFlightRouteRequest);
  }

  public async ignoreAnalysisFile(analysisId: string, fileName: string, ignore: boolean): Promise<void> {
    await this.post(`/auth/analysis/${analysisId}/file/${fileName}/ignore`, { ignore });
  }

  public async ignoreAnalysisFiles(
    analysisId: string,
    files: AnalysisFilesSearchRequest,
    ignore: boolean,
    expectedCount: number,
  ): Promise<void> {
    await this.post(`/auth/analysis/${analysisId}/files/ignore?expected_count=${expectedCount}`, { files, ignore });
  }

  // Slow endpoint (budget 20 s): trigger it from an <app-button long-loading-time>
  public getAnalysisMonitoring(
    queryParams?: { technology_id?: number; assigned_user_id?: string }
  ): Promise<AnalysisMonitoringEntry[]> {
    return this.get(`/auth/analysis-monitoring`, queryParams).then(
      response =>
        typeof response == "string"
          ? {}
          : response /* The 204 (NO CONTENT) response from the backend causes response to be a string */
    );
  }

  public getRunningEvaluations(analysisId: string, evalBatchNum?: number | null): Promise<RunningEvaluationSchema[]> {
    return this.get(`/auth/analysis/${analysisId}/running-evaluations`, {
      ...(evalBatchNum != null ? { eval_batch_num: evalBatchNum } : {}),
    });
  }

  public getAnalysisConfigYaml(analysisId: string, evalBatchNum?: number): Promise<AnalysisConfigYamlResponse> {
    return this.get(`/auth/analysis-config-yaml`, {
      analysis_id: analysisId,
      ...(evalBatchNum !== undefined ? { eval_batch_num: evalBatchNum } : {}),
    });
  }

  public async putAnalysisConfigYaml(
    analysisId: string,
    content: string,
    evalBatchNum?: number,
    artifactCurrentRevisions: Record<string, string> = {},
  ): Promise<void> {
    await this.put(`/auth/analysis-config-yaml`, {
      analysis_id: analysisId,
      ...(evalBatchNum !== undefined ? { eval_batch_num: evalBatchNum } : {}),
      content,
      artifact_current_revisions: artifactCurrentRevisions,
    });
  }

  public getOpsArtifacts(analysisId: string, evalBatchNum: number | null): Promise<OpsArtifactSlot[]> {
    return this.get(`/auth/analysis-ops-artifacts`, {
      analysis_id: analysisId,
      ...(evalBatchNum !== null ? { eval_batch_num: evalBatchNum } : {}),
    });
  }

  public getOpsArtifactFiles(
    analysisId: string,
    evalBatchNum: number | null,
    slot: string,
    revision: string,
  ): Promise<OpsArtifactFile[]> {
    return this.get(`/auth/analysis-ops-artifacts/files`, {
      analysis_id: analysisId,
      ...(evalBatchNum !== null ? { eval_batch_num: evalBatchNum } : {}),
      slot,
      revision,
    });
  }

  private opsArtifactFileParams(file: OpsArtifactFileRef): Record<string, string | number> {
    return {
      analysis_id: file.analysisId,
      ...(file.evalBatchNum !== null ? { eval_batch_num: file.evalBatchNum } : {}),
      slot: file.slot,
      revision: file.revision,
      name: file.name,
    };
  }

  public getOpsArtifactContent(file: OpsArtifactFileRef): Promise<Blob> {
    return this.get(`/auth/analysis-ops-artifacts/content`, this.opsArtifactFileParams(file), { responseType: "blob" });
  }

  public getOpsArtifactRasterInfo(file: OpsArtifactFileRef): Promise<OpsRasterInfo> {
    return this.get(`/auth/analysis-ops-artifacts/raster-info`, this.opsArtifactFileParams(file));
  }

  public getOpsArtifactRasterPreview(file: OpsArtifactFileRef, rasterInfo: OpsRasterInfo): Promise<Blob> {
    return this.get(
      `/auth/analysis-ops-artifacts/raster-preview`,
      { ...this.opsArtifactFileParams(file), ...this.opsRasterDisplayParams(rasterInfo) },
      { responseType: "blob" },
    );
  }

  // The tile server authenticates the tile with the token, so the request needs no headers (and no CORS preflight).
  public getOpsArtifactRasterTile(tileToken: string, tileCoord: { z: number; x: number; y: number }): Promise<Blob> {
    return this.getWithoutAuthHeaders(
      `${tileServerBaseUrl}/auth/ops-artifact-tile/${tileCoord.z}/${tileCoord.x}/${tileCoord.y}.png`,
      undefined,
      { responseType: "blob", params: { token: tileToken } },
    );
  }

  // The stretch the raster info computed once for the whole raster, so every tile is drawn with the same one
  private opsRasterDisplayParams(rasterInfo: OpsRasterInfo): Record<string, string> {
    const { rescale, tone_curve: toneCurve } = rasterInfo;
    return {
      ...(rescale ? { rescale: rescale.join(",") } : {}),
      ...(toneCurve ? { tone_curve: [toneCurve.min, toneCurve.max, toneCurve.knee, toneCurve.gamma].join(",") } : {}),
    };
  }

  public getAnalysisConfigYamlAvailability(items: Array<{ analysis_id: string; eval_batch_num?: number }>): Promise<AnalysisConfigYamlAvailabilityEntry[]> {
    return this.post(`/auth/analysis-config-yaml/availability`, { items });
  }

  public async finishRunningTask(analysisId: string): Promise<void> {
    await this.post(`/auth/analysis/${analysisId}/finish-running-task`);
  }

  public async getUploadingUsers(analysisId: string): Promise<SimpleUserSchema[]> {
    return this.get(`/auth/analysis/${analysisId}/uploading-users`);
  }

  public async getLastAnalysis(plantId: string, lastDate?: string): Promise<SimpleAnalysisSchema> {
    return await this.get(`/auth/last-analysis/${plantId}`, lastDate && { date: lastDate });
  }

  public async getAnalysisVisualMissionCoverage(analysisId: string): Promise<any> {
    return await this.get(`/auth/analysis/${analysisId}/visual-mission-coverage`);
  }

  /**
   * data_check_task_id is set when nothing was left to cover, but the data checks are outdated.
   * Slow endpoint (budget 30 s): trigger it from an <app-button long-loading-time>
   */
  public async generateAnalysisVisualMissionCoverage(
    analysisId: string,
    force?: boolean,
  ): Promise<{ task_id: string | null; data_check_task_id?: string | null }> {
    return await this.post(`/auth/analysis/${analysisId}/visual-mission-coverage/generate${(force ? "?force=1" : "")}`);
  }

  public async getAnalysisStillImagesPoints(analysisId: string): Promise<any> {
    return await this.get(`/auth/analysis/${analysisId}/still-images-points`);
  }

  public async getAnalysisDataChecks(analysisId: string): Promise<AnalysisDataCheckSchema[]> {
    return await this.get(`/auth/analysis/${analysisId}/data-quality`);
  }

  public async generateAnalysisDataChecks(analysisId: string): Promise<{ task_id: string }> {
    return await this.post(`/auth/analysis/${analysisId}/data-quality/generate`);
  }

  /** The coverage and data checks tasks of the analysis while they are queued or running. */
  public async getAnalysisDataCheckRunningTasks(analysisId: string): Promise<DataCheckRunningTasksSchema> {
    return await this.get(`/auth/analysis/${analysisId}/data-quality/running-tasks`);
  }


  public async getAnalysisSharedUsers(analysisId: string): Promise<SimpleUserRoleSchema[]> {
    return this.get(`/auth/analysis/${analysisId}/shared-users`);
  }

  public async setAnalysisSharedUsers(analysisId: string, userIds: string[]): Promise<void> {
    await this.post(`/auth/analysis/${analysisId}/shared-users`, { user_ids: userIds });
  }


  // Analysis invoice

  public async getAvailableInvoiceStates(): Promise<InvoiceStateSchema[]> {
    return this.get(`/auth/invoice-states`);
  }

  public async updateAnalysisInvoice(
    analysisId: string,
    updateData: UpdateAnalysisInvoiceRequest
  ): Promise<void> {
    await this.post(`/auth/analysis/${analysisId}/invoice`, updateData);
  }

  public async updateAnalysisMwUsageOverride(analysisId: string, mwUsageOverride: number | null): Promise<void> {
    await this.post(`/auth/analysis/${analysisId}/mw-usage-override`, { mw_usage_override: mwUsageOverride });
  }

  public async updateAnalysisCreditUsageOverride(analysisId: string, creditUsageOverride: number | null): Promise<void> {
    await this.post(`/auth/analysis/${analysisId}/credit-usage-override`, { credit_usage_override: creditUsageOverride });
  }

  //
  // Analysis result
  //

  public async importAnalysisResult(
    jsonFile: File | null,
    analysisId: string,
    imageFilesZipName?: string,
    deleteBefore?: boolean,
    evalBatchNum?: number,
    waitForever?: boolean,
  ): Promise<TaskSchema> {
    const formData = {};
    if (jsonFile) {
      formData["json_file"] = jsonFile;
    }
    if (imageFilesZipName) {
      formData["images_zip_filename"] = imageFilesZipName;
    }
    if (deleteBefore) {
      formData["delete_before"] = 1
    }
    if (evalBatchNum) {
      formData["eval_batch_num"] = evalBatchNum
    }
    if (waitForever) {
      formData["wait_forever"] = 1
    }

    return await this.postForm(`/auth/import-analysis-result/${analysisId}`, formData);
  }

  public async uploadImportAnalysisResultImageFiles(
    analysisId: string,
    imageFilesZip: File,
    uploadTmpId: string,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
  ): Promise<void> {
    await this.postForm(
      `/auth/import-analysis-result/${analysisId}/upload-images`, 
      { image_files_zip: imageFilesZip, upload_tmp_id: uploadTmpId },
      onUploadProgress
    );
  }

  public getSpecificAnalysisResult<T>(
    analysisResultId: string,
    componentId: number,
    params: TableRequest,
    filterParams?: TableFilterRequest
  ): Promise<TableResultSchema<T>> {
    return this.get(
      `/auth/analysis-result/${analysisResultId}/${componentId}${this.getQueryParams(
        params
      )}${this.getEncodedAnalysisResultFilterParams(filterParams)}`
    );
  }

  public getAnalysisResultHistogram(
    analysisResultId: string,
    componentId: number,
    params: HistogramRequest,
    filterParams?: TableFilterRequest,
  ): Promise<AnalysisResultHistogramBinSchema[]> {
    return this.get(
      `/auth/analysis-result/${analysisResultId}/${componentId}/histogram${this.getQueryParams(
        params
      )}${this.getEncodedAnalysisResultFilterParams(filterParams)}`
    );
  }

  public getSpecificAnalysisResultDTIdsFiltered<T>(
    analysisResultId: string,
    componentId: number,
    params: TableRequest,
    dtIds: string[],
    filterParams?: TableFilterRequest,
  ): Promise<TableResultSchema<T>> {
    return this.post(
      `/auth/analysis-result/${analysisResultId}/${componentId}${this.getQueryParams(
        params
      )}${this.getEncodedAnalysisResultFilterParams(filterParams)}`,
      { dt_ids: dtIds },
    );
  }

  // Slow endpoint (budget 60 s), used as download link
  public getSpecificAnalysisResultCsvUrl(
    analysisResultId: string,
    componentId: number,
    params: TableRequest,
    filterParams?: TableFilterRequest,
    csvMappings?: { [key: string]: string }
  ): string {
    const encodedCsvMappings =
      (csvMappings && `&csv_mappings=${encodeURIComponent(this.getQueryParams(csvMappings).substring(1))}`) || "";

    return `${apiBaseUrl}/auth/analysis-result/${analysisResultId}/${componentId}${this.getQueryParams(
      params
    )}&csv=1${encodedCsvMappings}${this.getEncodedAnalysisResultFilterParams(filterParams)}`;
  }

  // Slow endpoint (budget 60 s), used as download link
  public getSpecificAnalysisResultComparedCsvUrl(
    analysisResultId: string,
    componentId: number,
    compareAnalysisResultId: string,
    params: TableRequest,
    filterParams?: TableFilterRequest,
    csvMappings?: { [key: string]: string }
  ): string {
    const encodedCsvMappings =
      (csvMappings && `&csv_mappings=${encodeURIComponent(this.getQueryParams(csvMappings).substring(1))}`) || "";

    return `${apiBaseUrl}/auth/analysis-result/compare/${analysisResultId}/${componentId}/${compareAnalysisResultId}${this.getQueryParams(
      params
    )}&csv=1${encodedCsvMappings}${this.getEncodedAnalysisResultFilterParams(filterParams)}`;
  }

  public getSpecificAnalysisResultCompared<T>(
    analysisResultId: string,
    componentId: number,
    compareAnalysisResultId: string,
    params: TableRequest,
    filterParams?: TableFilterRequest
  ): Promise<TableResultSchema<T>> {
    return this.get(
      `/auth/analysis-result/compare/${analysisResultId}/${componentId}/${compareAnalysisResultId}${this.getQueryParams(
        params
      )}${this.getEncodedAnalysisResultFilterParams(filterParams)}`
    );
  }

  public getAnalysisResultHistory<T>(
    plantId: string,
    componentId: number,
    params: TableRequest,
    filterParams?: TableFilterRequest,
    keyFigureId?: ApiKeyFigure,
  ): Promise<TableResultSchema<AnalysisResultHistorySchema>> {
    return this.get(`/auth/analysis-results/history/${plantId}/${componentId}${this.getQueryParams(
        keyFigureId ? { key_figure_id: keyFigureId, ...params } : params
      )}
      ${this.getEncodedAnalysisResultFilterParams(filterParams)}`);
  }

  public getAnalysisResultHistoryUnlimited(
    plantId: string,
    componentId: number,
    params: TableRequest & { limit?: undefined },
    filterParams?: TableFilterRequest,
    keyFigureId?: ApiKeyFigure,
  ): Promise<AnalysisResultHistorySchema[]> {
    return this.getAnalysisResultHistory(
      plantId,
      componentId,
      params,
      filterParams,
      keyFigureId,
    ) as unknown as Promise<AnalysisResultHistorySchema[]>;
  }

  public getAnalysisResultFiles(analysisResultId: string): Promise<AnalysisResultFileSchema[]> {
    return this.get(`/auth/analysis-result/${analysisResultId}/files`);
  }

  public getAnalysisResultOmoFiles(analysisResultId: string): Promise<AnalysisResultOmoFileSchema[]> {
    return this.get(`/auth/analysis-result/${analysisResultId}/omo-files`);
  }

  public async toggleAnalysisResultOmoFileHidden(analysisResultId: string, omoFileId: string): Promise<void> {
    await this.post(`/auth/analysis-result/${analysisResultId}/omo-file/${omoFileId}/toggle-hidden`);
  }

  public async getAnalysisResults(plantId: string, allKeyFigures = false): Promise<AnalysisResultDetailedSchema[]> {
    const analysisResults = await this.get(
      `/auth/plant/${plantId}/analysis-results`, 
      allKeyFigures ? { all_key_figures: 1 } : undefined
    );

    for (const analysisResult of analysisResults) {
      this.replaceKeyFigureIRWithGlassTubeTemp(analysisResult);
    }

    return analysisResults;
  }

  public updateAnalysisResult(analysisResultId: string, updates: { release: boolean, eval_batch_num?: number }): Promise<AnalysisResultDetailedSchema> {
    return this.post(`/auth/analysis-result/${analysisResultId}`, updates);
  }

  public setAnalysisResultReadyForReview(analysisResultId: string, updates: { ready: boolean, eval_batch_num?: number }): Promise<AnalysisResultDetailedSchema> {
    return this.post(`/auth/analysis-result/${analysisResultId}/ready-for-review`, updates);
  }

  public getAnalysisResultFileUrl(analysisResultFileId: string): Promise<{ url: string }> {
    return this.get(`/auth/analysis-result/result-file/${analysisResultFileId}`);
  }

  public getExportAnalysisResultFileUrl(analysisId: string, ebNum?: number | null): string {
    return `${apiBaseUrl}/auth/export-analysis-result/${analysisId}${ebNum ? '?eval_batch_num=' + ebNum : ''}`;
  }

  public async modifyAnalysisResultValue(
    analysisResultId: string,
    modifyRequest: ModfifyAnalysisResultValueRequest,
  ): Promise<ChangedAnalysisResultsSchema> {
    return await this.post(`/auth/analysis-result/${analysisResultId}/modify-value`, modifyRequest);
  }

  public async getHotJunctionBoxDeltaTemperatures(
    analysisResultId: string,
  ): Promise<HotJunctionBoxDeltaTemperaturesSchema> {
    return await this.get(`/auth/analysis-result/${analysisResultId}/hot-junction-box-delta-temperatures`);
  }

  public async discardHotJunctionBoxes(
    analysisResultId: string,
    deltaTemperatureThreshold: number,
  ): Promise<ChangedAnalysisResultsSchema> {
    return await this.post(`/auth/analysis-result/${analysisResultId}/discard-hot-junction-boxes`, {
      delta_temperature_threshold: deltaTemperatureThreshold,
    });
  }

  public async getAnalysisResultChangeHistory(
    analysisResultId: string,
    dtId?: string,
    changedBy?: ChangedBy,
  ): Promise<AnalysisResultChangeHistorySchema[]> {
    return await this.get(
      `/auth/analysis-result/${analysisResultId}/change-history`,
      {
        ...(dtId ? { dt_id: dtId } : {}),
        ...(changedBy ? { changed_by: changedBy } : {}),
      },
    );
  }

  /** "evalBatchNums" limits the numbers to these eval batches; omitted for all of them. */
  public async getAnalysisResultAIPerformance(
    analysisResultId: string,
    evalBatchNums?: number[],
  ): Promise<AIPerformanceSchema> {
    return await this.get(
      `/auth/analysis-result/${analysisResultId}/ai-performance`,
      evalBatchNums ? { eval_batch_nums: evalBatchNums.join(",") } : {},
    );
  }

  public async getAnalysisResultRelabelingDuration(analysisResultId: string): Promise<RelabelingDurationSchema> {
    return await this.get(`/auth/analysis-result/${analysisResultId}/relabeling-duration`);
  }

  public async undoAnalysisResultValueChange(
    analysisResultChangeHistoryId: string,
  ): Promise<ChangedAnalysisResultsSchema> {
    return await this.post(`/auth/analysis-result-change-history/${analysisResultChangeHistoryId}/undo`);
  }

  public async isAnalysisResultUnmeasuredHolder(analysisResultId: string): Promise<{ is_unmeasured_holder: boolean }> {
    return await this.get(`/auth/analysis-result/${analysisResultId}/is-unmeasured-holder`);
  }

  public async refreshUnmeasuredOfNewerAnalyses(analysisResultId: string): Promise<void> {
    await this.post(`/auth/analysis-result/${analysisResultId}/refresh-unmeasured-of-newer-analyses`);
  }

  //
  // Analysis result release
  //

  public async setAnalysisReleaseNotes(analysisResultId: string, releaseNotes: string): Promise<void> {
    await this.postForm(`/auth/analysis-result/${analysisResultId}/release-notes`, { release_notes: releaseNotes });
  }

  public async getAnalysisReleaseNotes(analysisResultId: string): Promise<AnalysisResultReleaseNotesSchema | null> {
    return this.get(`/auth/analysis-result/${analysisResultId}/release-notes`);
  }

  public async getAnalysisReleaseNotesUsers(analysisResultId: string): Promise<SimpleUserRoleSchema[]> {
    return this.get(`/auth/analysis-result/${analysisResultId}/release-notes-users`);
  }

  public async sendAnalysisReleaseNotes(analysisResultId: string, userIds: string[]): Promise<void> {
    await this.post(`/auth/analysis-result/${analysisResultId}/send-release-mail`, { recipient_user_ids: userIds })
  }

  public async getSentAnalysisReleaseMails(analysisResultId: string): Promise<AnalysisResultReleaseMailSchema[]> {
    return this.get(`/auth/analysis-result/${analysisResultId}/sent-release-mails`)
  }

  public getAnalysisResultOrthomosaicTileXYZUrl(analysisResultFileId: string, keyFigureId: number, urlIndex: number): string {
    return `${tileServerBaseUrl}/auth/orthomosaic/analysis-result/${analysisResultFileId}/${keyFigureId}/${urlIndex}/{x}/{y}/{z}.png`;
  }

  public async getAnalysisResultOrthomosaicUrlsToken(
    analysisResultId: string,
    keyFigureId: number
  ): Promise<OmoUrlToken> {
    return this.get(`/auth/orthomosaic/analysis-result/${analysisResultId}/${keyFigureId}/urls`);
  }

  public async validateAnalysisResultOrthomosaicUrlsToken(
    analysisResultId: string,
    keyFigureId: number,
    omoUrlsToken: string,
  ): Promise<{ valid: boolean }> {
    return this.post(`/auth/orthomosaic/analysis-result/${analysisResultId}/${keyFigureId}/urls/validate`, { url_token: omoUrlsToken });
  }

  public async getAnalysisResultOrthomosaicTile(tileXYZUrl: string, imageUrlsToken: string): Promise<Blob> {
    // The tile server authenticates the tile with "omo_token", so the request needs no headers.
    // Passed as an axios param, which is appended correctly to a URL that has a query already
    // (see getOrthomosaicTemperatureRangeTileXYZUrl).
    return this.getWithoutAuthHeaders(tileXYZUrl, undefined, { responseType: "blob", params: { omo_token: imageUrlsToken } });
  }

  private getOrthomosaicTemperatureUrl(analysisResultFileId: string, keyFigureId: number, urlIndex: number): string {
    return `${tileServerBaseUrl}/auth/orthomosaic-temperature/analysis-result/${analysisResultFileId}/${keyFigureId}/${urlIndex}`;
  }

  /** Tiles of the IR orthomosaic with every pixel outside the temperature range blacked out or hidden. */
  public getOrthomosaicTemperatureRangeTileXYZUrl(
    analysisResultFileId: string,
    keyFigureId: number,
    urlIndex: number,
    range: TemperatureRange,
  ): string {
    const query = [
      range.min !== null ? `t_min=${range.min}` : null,
      range.max !== null ? `t_max=${range.max}` : null,
      `outside=${range.outside}`,
    ].filter(param => param !== null).join("&");
    return `${this.getOrthomosaicTemperatureUrl(analysisResultFileId, keyFigureId, urlIndex)}/range/{x}/{y}/{z}.png?${query}`;
  }

  /** Tiles holding the temperatures of the IR orthomosaic, for the WebApp to colour (see TemperatureDisplayLayer). */
  public getOrthomosaicTemperatureDisplayTileXYZUrl(analysisResultFileId: string, keyFigureId: number, urlIndex: number): string {
    return `${this.getOrthomosaicTemperatureUrl(analysisResultFileId, keyFigureId, urlIndex)}/display/{x}/{y}/{z}.png`;
  }

  public async getOrthomosaicPointTemperature(
    omoUrlToken: OmoUrlToken,
    keyFigureId: number,
    urlIndex: number,
    lonLat: number[],
  ): Promise<PointTemperatureSchema> {
    return this.getWithoutAuthHeaders(
      `${this.getOrthomosaicTemperatureUrl(omoUrlToken.analysis_result_file_id, keyFigureId, urlIndex)}/point`,
      { omo_token: omoUrlToken.token, lon: lonLat[0], lat: lonLat[1] },
    );
  }

  public async getOrthomosaicTemperatureInfo(
    omoUrlToken: OmoUrlToken,
    keyFigureId: number,
    urlIndex: number,
  ): Promise<TemperatureInfoSchema> {
    return this.getWithoutAuthHeaders(
      `${this.getOrthomosaicTemperatureUrl(omoUrlToken.analysis_result_file_id, keyFigureId, urlIndex)}/info`,
      { omo_token: omoUrlToken.token },
    );
  }

  /** "lonLatRing": the polygon in WGS84. A GET, because the tile server only answers GET requests. */
  public async getOrthomosaicAreaTemperatures(
    omoUrlToken: OmoUrlToken,
    keyFigureId: number,
    urlIndex: number,
    lonLatRing: number[][],
  ): Promise<AreaTemperaturesSchema> {
    return this.getWithoutAuthHeaders(
      `${this.getOrthomosaicTemperatureUrl(omoUrlToken.analysis_result_file_id, keyFigureId, urlIndex)}/area`,
      { omo_token: omoUrlToken.token, polygon: JSON.stringify(lonLatRing) },
    );
  }

  public getOrthomosaicDownloadUrl(analysisResultId: string, keyFigureId: number) {
    return `${apiBaseUrl}/auth/orthomosaic/analysis-result/${analysisResultId}/${keyFigureId}/download`;
  }

  public getOmoReportDownloadUrl(analysisResultId: string, filename: string): Promise<{ url: string }> {
    return this.get(`/auth/orthomosaic/analysis-result/${analysisResultId}/download-omo-report/${filename}`);
  }

  public async getAnalysisResultComponentOrthomosaicTile(
    analysisResultId: string,
    dtId: string,
    tokens: { rgb_omo_token?: string; ir_omo_token?: string; soiling_omo_token?: string; }
  ): Promise<{ rgb?: string, ir?: string, soiling?: string }> {
    return this.get(`/auth/orthomosaic/analysis-result/${analysisResultId}/${dtId}`, tokens);
  }

  //
  // 3D models (.glb)
  //

  /**
   * Returns presigned S3 URLs for the textured 3D model (.glb) files of the given
   * key figure (PV_PLANT_MODEL_3D_RGB / PV_PLANT_MODEL_3D_IR). The viewer streams
   * the model directly from S3 (with download progress), so the file never passes
   * through the API server.
   */
  public getModel3dUrls(analysisResultId: string, keyFigureId: number): Promise<Model3dUrlsSchema> {
    return this.get(`/auth/model-3d/analysis-result/${analysisResultId}/${keyFigureId}/urls`);
  }

  /**
   * Same lookup as getModel3dUrls but with attachment disposition (browser download).
   */
  public getModel3dDownloadUrls(analysisResultId: string, keyFigureId: number): Promise<Model3dUrlsSchema> {
    return this.get(`/auth/model-3d/analysis-result/${analysisResultId}/${keyFigureId}/download`);
  }

  //
  // Task
  //
  
  public getTask(taskId: string): Promise<TaskSchema> {
    return this.get(`/auth/task/${taskId}`);
  }

  public getTaskOutputAsMessage(task: TaskSchema, defaultMsg = "") {
    if (task.output?.infos && task.output.infos.length > 0) {
      return `Task: <b>${task.name}</b><br>> ` +
        task.output.infos.join("<br>> ") +
        ((task.output.max_steps && `... (${task.output.step}/${task.output.max_steps})`) || "...");
    }

    return `Task: <b>${task.name}</b><br>` + defaultMsg;
  }

  public async launchTaskForAnalysis(launchTaskForAnalysisRequest: LaunchTaskForAnalysisRequest) {
    await this.post(`/auth/task/launch-task-for-analysis`, launchTaskForAnalysisRequest);
  }
  
  //
  // Plant
  //
  
  public getPlant(plantId: string): Promise<PlantSchema> {
    return this.get(`/auth/plant/${plantId}`);
  }

  public getPlants(queryParams?: { customer_id?: string }): Promise<SimplePlantSchema[]> {
    return this.get(`/auth/plants`, queryParams);
  }

  /** allCustomers: admins get the plants of all customers, not only of the customer they switched to */
  public getPlantsWithCustomers(allCustomers = false): Promise<SimplePlantSchema[]> {
    return this.get(`/auth/plants`, { with_customers: 1, all_customers: allCustomers ? 1 : 0 });
  }

  public getPlantsForTable(
    queryParams?: { technology_id?: number, plant_name?: string, customer_id?: string } & TableRequest & { limit: number },
  ): Promise<TableResultSchema<PlantTableSchema>> {
    return this.get(`/auth/plants-for-table`, queryParams);
  }

  public getCurrentSimplePlantsForTable(
    queryParams: TableRequest & { limit: number, technology_id?: number, ops_view?: boolean, ops_view_show_completed?: boolean, assigned_user_id?: string }
  ): Promise<TableResultSchema<SimplePlantWithLastActivitySchema>> {
    return this.get(`/auth/current-simple-plants-for-table`, queryParams);
  }

  public getAllPlants(): Promise<PlantSchema[]> {
    return this.get(`/auth/all-plants`);
  }

  public getAllPlantsForOrders(): Promise<SimplePlantForOrdersSchema[]> {
    return this.get(`/auth/all-plants-for-orders`);
  }

  public updatePlant(plantId: string, updatePlantRequest: UpdatePlantRequest): Promise<void> {
    return this.post(`/auth/plant/${plantId}`, updatePlantRequest);
  }

  public updatePlantFlightAreasAndTakeOffPositions(plantId: string, updatePlantFlightAreasAndTakeOffPositionsRequest: UpdatePlantFlightAreasAndTakeOffPositionsRequest): Promise<void> {
    return this.post(`/auth/plant/${plantId}/update-flight-areas-and-take-off-positions`, updatePlantFlightAreasAndTakeOffPositionsRequest);
  }

  public getPlantAreas(plantId: string): Promise<PlantAreaSchema[]> {
    return this.get(`/auth/plant/${plantId}/areas`);
  }

  public updatePlantAreas(plantId: string, updatePlantAreasRequest: UpdatePlantAreasRequest): Promise<PlantAreaSchema[]> {
    return this.post(`/auth/plant/${plantId}/areas`, updatePlantAreasRequest);
  }

  public suggestFlightAreaPolygons(suggestFlightAreaPolygonRequest: SuggestFlightAreaPolygonRequest): Promise<SuggestedFlightAreaPolygonsSchema> {
    return this.post(`/auth/flight-area/suggest-polygon`, suggestFlightAreaPolygonRequest);
  }

  public async createPlant(createPlantRequest: CreatePlantRequest): Promise<void> {
    await this.post(`/auth/plant`, createPlantRequest);
  }

  public async deletePlant(plantId: string): Promise<void> {
    await this.delete(`/auth/plant/${plantId}`);
  }

  public getAllSubfieldsOfPlant(plantId: string): Promise<string[]> {
    return this.get(`/auth/plant/${plantId}/subfields`);
  }

  public getAllPlantStatus(): Promise<PlantStatusSchema[]> {
    return this.get('/auth/plant-status');
  }

  public async updatePlantStatus(
    plantStatusId: string,
    updatePlantStatusRequest: UpdatePlantStatusRequest
  ): Promise<void> {
    await this.post(`/auth/plant-status/${plantStatusId}`, updatePlantStatusRequest);
  }

  public async getCustomComponentProperties(plantId: string): Promise<CustomComponentPropertySchema[]> {
    return await this.get(`/auth/plant/${plantId}/custom-component-properties`);
  }

  public async createCustomComponentProperty(plantId: string, ccpRequest: CustomComponentPropertyRequest) {
    await this.post(`/auth/plant/${plantId}/custom-component-property`, ccpRequest);
  }
  
  public async updateCustomComponentProperty(plantId: string, ccpId: string, ccpRequest: CustomComponentPropertyRequest) {
    await this.post(`/auth/plant/${plantId}/custom-component-property/${ccpId}`, ccpRequest);
  }

  public async deleteCustomComponentProperty(plantId: string, ccpId: string) {
    await this.delete(`/auth/plant/${plantId}/custom-component-property/${ccpId}`)
  }

  public getReleaseNoteImageUrl(release_notes_version: string, file_name: string): Promise<{ url: string }> {
    return this.get(`/auth/release-notes/${release_notes_version}/${file_name}`)    
  }

  public async enablePiFields(plantId: string, enablePiFieldsRequest: EnablePiFieldsRequest) {
    await this.post(`/auth/plant/${plantId}/enabled-pi-fields`, enablePiFieldsRequest);
  }

  public async disablePiFields(plantId: string, disablePiFieldsRequest: DisablePiFieldsRequest) {
    await this.post(`/auth/plant/${plantId}/disabled-pi-fields`, disablePiFieldsRequest);
  }

  public async getEnabledPiFields(plantId: string): Promise<EnabledPiFieldSchema[]> {
    return await this.get(`/auth/plant/${plantId}/enabled-pi-fields`);
  }

  public async getPlantBlueprints(plantId: string): Promise<PlantBlueprintSchema[]> {
    return await this.get(`/auth/plant/${plantId}/blueprints`);
  } 

  public async createPlantPdfBlueprint(plantId: string, createPlantBlueprintRequest: CreatePlantPdfBlueprintRequest): Promise<{ id: string }> {
    return await this.post(`/auth/plant/${plantId}/blueprint/pdf`, createPlantBlueprintRequest);
  }

  public async createPlantKmlBlueprint(plantId: string, kmlData: { file: File, name: string, opacity: string }): Promise<{ id: string }> {
    return await this.postForm(`/auth/plant/${plantId}/blueprint/kml`, kmlData);
  }

  public async updatePlantPdfBlueprint(
    plantId: string,
    plantBlueprintId: string,
    updatePlantPdfBlueprintRequest: UpdatePlantPdfBlueprintRequest,
  ): Promise<void> {
    await this.put(`/auth/plant/${plantId}/blueprint/${plantBlueprintId}/pdf/update`, updatePlantPdfBlueprintRequest);
  }

  public async getPlantPdfBlueprint(plantId: string, plantBlueprintId: string): Promise<PlantBlueprintPdfFileSchema> {
    return await this.get(`/auth/plant/${plantId}/blueprint/${plantBlueprintId}/pdf`);
  }

  public async getPlantKmlBlueprint(plantId: string, plantBlueprintId: string): Promise<Blob> {
    return await this.get(`/auth/plant/${plantId}/blueprint/${plantBlueprintId}/kml`, undefined, { responseType: "blob" });
  }

  public async deletePlantBlueprint(plantId: string, plantBlueprintId: string): Promise<void> {
    await this.delete(`/auth/plant/${plantId}/blueprint/delete/${plantBlueprintId}`);
  }

  // Slow endpoint (budget 20 s): trigger it from an <app-button long-loading-time>
  public async validateKmlFileNomenclature(plantId: string, kmlData: ValidateKmlNomenclatureImportRequest): Promise<any> {
    return await this.postForm(`/auth/plant/${plantId}/validate-kml-nomenclature`, kmlData);
  }

  // Slow endpoint (budget 90 s): trigger it from an <app-button long-loading-time>
  public async renamePcs(plantId: string, renamePcsRequest: RenamePcsRequest): Promise<Record<string, string>> {
    return await this.post(`/auth/plant/${plantId}/rename-pcs`, renamePcsRequest);
  }

  public async calculateMirrorTypes(plantId: string): Promise<{updated_count: number}> {
    return await this.post(`/auth/plant/${plantId}/mirror-types/calculate`);
  }

  public async resetMirrorTypes(plantId: string): Promise<{updated_count: number}> {
    return await this.post(`/auth/plant/${plantId}/mirror-types/reset`);
  }

  public async getOnlyAvailablePlantId(): Promise<{id: string | null}> {
    return this.get(`/auth/plant/only-available-id`);
  }

  public async validateDxfFile(plantId: string, file: File, onUploadProgress: (progressEvent: AxiosProgressEvent) => void): Promise<DxfValidationResult> {
    return this.postForm(`/auth/plant/${plantId}/validate-dxf-file`, { file }, onUploadProgress);
  }

  public createDigitTwin(plantId: string, createDigitTwinRequest: CreateDigitTwinRequest): Promise<TaskSchema> {
    return this.post(`/auth/plant/${plantId}/create-digital-twin`, createDigitTwinRequest);
  }

  public getPlantRunningTasks(plantId: string): Promise<TaskSchema[]> {
    return this.get(`/auth/plant/${plantId}/get-running-tasks`);
  }

  //
  // Keyfigures, Technology
  //

  public getAllKeyFigures(): Promise<KeyFigureSchema[]> {
    return this.get('/auth/key-figures');
  }

  private replaceKeyFigureIRWithGlassTubeTemp(analysisResult: AnalysisResultDetailedSchema): void {
    // IR_INTENSITY: Replaced by GLASS_TUBE_TEMPERATURE
    const ir_intensity_index = analysisResult.key_figures.findIndex(
      keyFigure => keyFigure.id === ApiKeyFigure.IR_INTENSITY_ID
    );
    if (
      ir_intensity_index != -1 &&
      analysisResult.key_figures.find(keyFigure => keyFigure.id === ApiKeyFigure.GLASS_TUBE_TEMPERATURE_ID)
    ) {
      analysisResult.key_figures.splice(ir_intensity_index, 1);
    }
  }

  public async getTechnologies(): Promise<TechnologySchema[]> {
    return await this.get(`/auth/technologies`);
  }

  public async getPerformanceIndicators(technologyId: ApiTechnology, locale: Locale = "en"): Promise<PerformanceIndicatorSchema[]> {
    return await this.get(`/auth/performance-indicators/${technologyId}?locale=${locale}`);
  }

  //
  // Core metadata (components, performance indicators and their classifications)
  //

  public async getCoreMetadataComponents(technologyId?: ApiTechnology): Promise<ComponentSchema[]> {
    return await this.get(`/auth/core-metadata/components`, technologyId ? { technology_id: technologyId } : undefined);
  }

  public async updateCoreMetadataComponent(
    componentId: number,
    updateComponentRequest: UpdateComponentRequest
  ): Promise<ComponentSchema> {
    return await this.post(`/auth/core-metadata/components/${componentId}`, updateComponentRequest);
  }

  public async getCoreMetadataPerformanceIndicators(
    technologyId?: ApiTechnology,
    componentId?: ApiComponent
  ): Promise<CoreMetadataPerformanceIndicatorSchema[]> {
    return await this.get(`/auth/core-metadata/performance-indicators`, {
      ...(technologyId ? { technology_id: technologyId } : {}),
      ...(componentId ? { component_id: componentId } : {}),
    });
  }

  public async updateCoreMetadataPerformanceIndicator(
    piId: number,
    updatePerformanceIndicatorRequest: UpdatePerformanceIndicatorRequest
  ): Promise<CoreMetadataPerformanceIndicatorSchema> {
    return await this.post(`/auth/core-metadata/performance-indicators/${piId}`, updatePerformanceIndicatorRequest);
  }

  //
  // Geo visualization
  //

  public getComponentsGeoVisual(
    plantId: string,
    componentIds: ApiComponent[],
    extent?: { min_long: number, max_long: number, min_lat: number, max_lat: number },
  ): Promise<any> {
    return this.get(`/auth/geo-visual/${plantId}/components`, { ids: componentIds, ...(extent ? extent : {}) });
  }

  public getComponentsGeoVisualCsv(
    plantId: string,
    componentIds: ApiComponent[],
  ): Promise<string> {
    return this.get(`/auth/geo-visual/${plantId}/components-csv`, { ids: componentIds }, { responseType: "text" });
  }

  public getKeyFiguresGeoVisual(
    plantId: string,
    analysisResultId: string,
    keyFiguresId: ApiKeyFigure,
    query_params?: GeoVisualCspPtcQuery | GeoVisualPvQuery
  ): Promise<any> {
    return this.get(`/auth/geo-visual/${plantId}/${analysisResultId}/key-figure/${keyFiguresId}`, query_params);
  }

  public getGeoVisualDTIdsFiltered(
    plantId: string,
    analysisResultId: string,
    keyFiguresId: ApiKeyFigure,
    query_params?: GeoVisualCspPtcQuery | GeoVisualPvQuery,
    dtIds?: string[],
  ): Promise<any> {
    return this.post(
      `/auth/geo-visual/${plantId}/${analysisResultId}/key-figure/${keyFiguresId}`, 
      (dtIds ? { dt_ids: dtIds } : {}),
      undefined,
      query_params,
    );
  }

  public getKeyFiguresGeoVisualCompare(
    plantId: string,
    analysisResultId: string,
    compareAnalysisResultId: string,
    keyFiguresId: ApiKeyFigure,
    query_params?: GeoVisualQuery
  ): Promise<any> {
    return this.get(
      `/auth/geo-visual/compare/${plantId}/${analysisResultId}/${compareAnalysisResultId}/key-figure/${keyFiguresId}`,
      query_params
    );
  }

  public getAnalysisResultGeoVisual(
    plantId: string,
    analysisResultId: string,
    componentId: number,
    params: TableRequest,
    filterParams?: TableFilterRequest,
    compareAnalysisResultId?: string,
    dtIds?: string[],
  ): Promise<any> {
    return this.get(
      `/auth/geo-visual/analysis-result/${plantId}/${analysisResultId}/component/${componentId}${this.getQueryParams(
        {
          ...params,
          ...(compareAnalysisResultId ? { compare_analysis_result_id: compareAnalysisResultId } : {}),
          ...(dtIds?.length ? { dt_ids: dtIds.join(",") } : {}),
        }
      )}${this.getEncodedAnalysisResultFilterParams(filterParams)}`
    );
  }

  public getGoogleMapsApiKey(): Promise<{ key: string }> {
    return this.get(`/auth/geo-visual/google-maps-api-key`);
  }

  //
  // Fieldgeometry - DT
  //

  public importFieldgeometry(
    file: File, 
    plantId: string, 
    clearBefore: boolean,
    evalBatchNum?: number | null,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
  ): Promise<TaskSchema> {
    const queryParams = this.getQueryParams({
      clear_before: clearBefore,
      ...(evalBatchNum ? { eval_batch_num: evalBatchNum } : {}),
    });
    return this.postForm(`/auth/fieldgeometry/${plantId}${queryParams}`, { file }, onUploadProgress);
  }

  public getFieldgeometryFileUrl(fieldgeometryId: string): Promise<{ url: string }> {
    return this.get(`/auth/fieldgeometry/${fieldgeometryId}/file-url`);
  }

  public cloneFieldgeometry(
    plantId: string,
    clonePlantRequest: ClonePlantRequest,
  ): Promise<TaskSchema> {
    return this.post(`/auth/fieldgeometry/${plantId}/clone`, clonePlantRequest);
  }

  public validateFieldgeometryDtIds(fieldgeometryId: string, dtIds: FieldgeoDtIdsValidationRequest): Promise<FieldgeoValidationResult> {
    return this.post(`/auth/fieldgeometry/${fieldgeometryId}/validate-dt-ids`, dtIds);
  }

  public async updateFieldgeometrySeverityLimits(
    fieldgeometryId: string,
    updateFieldgeometrySeverityLimitsRequest: UpdateFieldgeometrySeverityLimitsRequest,
  ) {
    await this.post(`/auth/fieldgeometry/${fieldgeometryId}/severity-limits`, updateFieldgeometrySeverityLimitsRequest);
  }

  //
  // VolaFly
  //

  public downloadVolaFlyAppUrl(sdk_version: string): Promise<{ url: string, version: string }> {
    return this.get(`${apiBaseUrl}/volafly-download/${sdk_version}`)    
  }

  public async getLatestVolaFlyVersion(): Promise<{ version: string }> {
    return this.get(`${apiBaseUrl}/volafly-version/latest`);
  }

  public async getVolaFlyLogs(
    queryParams: TableRequest & { limit: number },
  ): Promise<TableResultSchema<UserVolaflyLogSchema>> {
    return this.get(`/auth/volafly-logs`, queryParams);
  }

  /**
   * Short lived download URL, minted per request. It is never stored or shared, so it is
   * fetched right before the browser follows it.
   */
  public async getVolaFlyLogDownloadUrl(volaflyLogId: string): Promise<{ url: string }> {
    return this.get(`/auth/volafly-log/${volaflyLogId}/download-url`);
  }

  //
  // Customer
  //

  public getCustomers(): Promise<CustomerSchema[]> {
    return this.get(`/auth/customers`);
  }
  
  public getCustomersForTable(
    queryParams?: { customer_name?: string; f2c_user_id?: string; f2c_unassigned?: boolean } & TableRequest & { limit: number },
  ): Promise<TableResultSchema<CustomerSchema>> {
    return this.get(`/auth/customers-for-table`, queryParams);
  }

  public async assignCustomerF2cUser(customerId: string, assignF2cUserData: AssignCustomerF2cUserRequest): Promise<void> {
    await this.post(`/auth/customer/${customerId}/f2c-user`, assignF2cUserData);
  }

  public async createCustomer(customerRequest: CreateCustomerRequest): Promise<{ id: string }> {
    return await this.post(`/auth/customer`, customerRequest);
  }

  public async updateCustomer(customerId: string, customerRequest: UpdateCustomerRequest): Promise<void> {
    await this.post(`/auth/customer/${customerId}`, customerRequest);
  }

  public async deleteCustomer(customerId: string): Promise<void> {
    await this.delete(`/auth/customer/${customerId}`);
  }

  public async setCustomerLogo(customerId: string, logo: File, onUploadProgressEvent?: (progressEvent: AxiosProgressEvent) => void) {
    await this.postForm(`/auth/customer/${customerId}/logo`, { logo }, onUploadProgressEvent);
  }

  public async updateOnlyOwnCustomer(customerRequest: UpdateOnlyOwnCustomerRequest): Promise<void> {
    await this.post(`/auth/customer/update`, customerRequest);
  }

  public async getOnlyOwnCustomer(): Promise<OnlyOwnCustomerSchema> {
    return this.get(`/auth/customer`);
  }

  /** The F2C of the own customer, or null if none is assigned. */
  public async getOwnCustomerAccountManager(): Promise<SimpleUserSchema | null> {
    return this.get(`/auth/customer/account-manager`);
  }

  /** The credits of the own customer's current Wallet orders, or null without one. */
  public async getOwnCustomerWalletCredits(): Promise<WalletCreditsSchema | null> {
    return this.get(`/auth/customer/wallet-credits`);
  }

  /** The current Wallet orders of the own customer and the email of its F2C. */
  public async getOwnCustomerWalletOrdersInfo(): Promise<WalletOrdersInfoSchema> {
    return this.get(`/auth/customer/wallet-orders-info`);
  }

  /** Whether the own customer has ever booked an order. */
  public async getOwnCustomerHasOrders(): Promise<boolean> {
    return this.get(`/auth/customer/has-orders`);
  }

  public async getCustomerReleaseMailRecipients(customerId: string): Promise<SimpleUserRoleSchema[]> {
    return this.get(`/auth/customer/${customerId}/release-mail-recipients`);
  }

  public async getCustomerLogoImg(whiteLabelingVersion: string, customerId?: string): Promise<Blob> {
    return this.get(
      `/auth/customer/logo-img/${whiteLabelingVersion}`,
      customerId ? { "customer_id": customerId } : undefined,
      { responseType: "blob" }
    );
  }

  public async getCustomerWhiteLabelColors(whiteLabelingVersion: string): Promise<WhiteLabelColors | null> {
    return this.get(`/auth/customer/white-labeling-colors/${whiteLabelingVersion}`);
  }

  //
  // QFly Server
  //

  public async getQFlyServers(): Promise<QFlyServerSchema[]> {
    return this.get(`/auth/qfly-servers`);
  }

  public async startQFlyServer(qflyServerUpdateRequest: QFlyServerUpdateRequest): Promise<void> {
    await this.post(`/auth/qfly-server/start`, qflyServerUpdateRequest);
  }

  public async stopQFlyServer(qflyServerUpdateRequest: QFlyServerUpdateRequest): Promise<void> {
    await this.post(`/auth/qfly-server/stop`, qflyServerUpdateRequest);
  }

  public async updateQFlyServer(qflyServerUpdateRequest: QFlyServerUpdateRequest): Promise<void> {
    await this.post(`/auth/qfly-server/update`, qflyServerUpdateRequest);
  }

  //
  // Runpod.io
  //

  public async getRunpodServers(): Promise<RunpodServerSchema[]> {
    return this.get(`/auth/runpod-servers`);
  }

  // Slow endpoint (budget 60 s): trigger it from an <app-button long-loading-time>
  public async createRunpodServer(createRunpodServerRequest: CreateRunpodServerRequest): Promise<RunpodServerSchema> {
    return await this.post(`/auth/create-runpod-server`, createRunpodServerRequest);
  }

  public async startRunpodServer(runpodServerId: string): Promise<void> {
    await this.post(`/auth/runpod-server/${runpodServerId}/start`);
  }

  // Slow endpoint: Runpod's log stream is read until the pod has been quiet for a few seconds
  /**
   * after: the cursor of a previous response, to get only the lines written since.
   * tail: lines per source, at most RUNPOD_LOG_MAX_TAIL_LINES; the backend's default if omitted.
   */
  public async getRunpodServerLogs(runpodServerId: string, after: string | null = null, tail?: number): Promise<RunpodLogsSchema> {
    return this.get(`/auth/runpod-server/${runpodServerId}/logs`, withoutUnsetParams({ after, tail }));
  }

  /** Without jobId the backend picks the running job of the task */
  public async getTaskBatchLogs(taskId: string, jobId: string | null, after: string | null): Promise<BatchJobLogsSchema> {
    return this.get(`/auth/task/${taskId}/batch-logs`, withoutUnsetParams({ job_id: jobId, after }));
  }

  public async stopRunpodServer(runpodServerId: string): Promise<void> {
    await this.post(`/auth/runpod-server/${runpodServerId}/stop`);
  }

  public async terminateRunpodServer(runpodServerId: string): Promise<void> {
    await this.post(`/auth/runpod-server/${runpodServerId}/terminate`);
  }

  public async updateRunpodServer(runpodServerUpdateRequest: RunpodServerUpdateRequest): Promise<void> {
    await this.post(`/auth/runpod-server/update`, runpodServerUpdateRequest);
  }

  public async getRunpodNetworkVolumes(): Promise<RunpodNetworkVolumeSchema[]> {
    return this.get(`/auth/runpod-network-volumes`);
  }

  public async getRunpodAvailability(runpodAvailabilityRequest: RunpodAvailabilityRequest): Promise<RunpodAvailabilitySchema> {
    return this.get(`/auth/runpod-availability`, withoutUnsetParams({
      network_volume_id: runpodAvailabilityRequest.network_volume_id,
      gpu_count: runpodAvailabilityRequest.gpu_count,
      allowed_cuda_versions: runpodAvailabilityRequest.allowed_cuda_versions?.join(","),
      vcpu_count: runpodAvailabilityRequest.vcpu_count,
    }));
  }

  public async getMetashapeLicenseUsage(): Promise<MetashapeLicenseUsageSchema> {
    return this.get(`/auth/metashape-license-usage`);
  }

  //
  // Observation - (Reference Measurements are Observations)
  //

  public async getReferenceMeasurements(analysisId: string): Promise<ReferenceMeasurementsSchema> {
    return await this.get(`/auth/reference-measurements/${analysisId}`);
  }

  public async moveObservations(sourceAnalysisId: string, targetAnalysisId: string): Promise<void> {
    await this.post(`/auth/observations/move`, 
      { source_analysis_id: sourceAnalysisId, target_analysis_id: targetAnalysisId });
  }

  public async ignoreReferenceMeasurement(observationId: string, ignore: boolean): Promise<void> {
    await this.post(`/auth/reference-measurement/${observationId}/ignore`, { ignore });
  }

  public async getSummerizedOberservations(
    plantId: string,
    summerizedObservationRequest: SummerizedObservationRequest
  ): Promise<SummerizedDates> {
    return await this.get(`/auth/plant/${plantId}/summerized-observations`, summerizedObservationRequest);
  }

  public async createObservation(
    plantId: string,
    observation: ObservationRequest,
    feature: FeatureLike
  ): Promise<CreatedObservationSchema> {
    return await this.post(`/auth/plant/${plantId}/observation`, observation);
  }

  public async addObservations(plantId: string, observations: ObservationsRequest): Promise<CreatedObservationSchema[]> {
    return await this.post(`/auth/plant/${plantId}/observations`, observations);
  }

  public async updateObservation(plantId: string, observationId: string, observation: ObservationRequest, feature: FeatureLike) {
    await this.post(`/auth/plant/${plantId}/observation/${observationId}`, observation);
  }

  public async deleteObservation(plantId: string, observationId: string, feature: FeatureLike): Promise<void> {
    await this.delete(`/auth/plant/${plantId}/observation/${observationId}`);
  }

  public async uploadObservationImages(
    plantId: string,
    observationId: string,
    images: File[],
    component: ApiComponent,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  ) {
    const postImages: Record<string, File> = {};
    for (const file of images) {
      postImages[file.name] = file;
    }

    await this.postForm(`/auth/plant/${plantId}/observation/${observationId}/images`, postImages, onUploadProgress)
  }

  public async getObservationsGeoVisualCcp(
    plantId: string,
    ccpId: string,
    fromDate: string,
    toDate: string,
    filterValue: ObservFilterValue
  ): Promise<any> {
    return await this.get(`/auth/geo-visual/observations/${plantId}/ccp/${ccpId}/${fromDate}/${toDate}`, 
      filterValue !== undefined ? { filter_value: filterValue } : undefined);
  }

  public async getObservationsGeoVisualPi(
    plantId: string,
    piId: ApiPI,
    fromDate: string,
    toDate: string,
    filterValue: ObservFilterValue,
  ): Promise<any> {
    return await this.get(`/auth/geo-visual/observations/${plantId}/pi/${piId}/${fromDate}/${toDate}`,
      filterValue !== undefined ? { filter_value: filterValue } : undefined);
  }

  public async getObservations(
    plantId: string,
    componentId: number,
    dFrom: string,
    dTo: string,
    params: TableRequest,
    filterParams?: TableFilterRequest,
  ): Promise<TableResultSchema<ObservationSchema, ObservationColumn>> {
    return await this.get(`/auth/plant/${plantId}/observations/${componentId}/${dFrom}/${dTo}${
      this.getQueryParams(params)}${this.getEncodedAnalysisResultFilterParams(filterParams)}`);
  }

  public getObservationsCsvUrl(
    plantId: string,
    componentId: number,
    dFrom: string,
    dTo: string,
    params: TableRequest,
    csvColumns: CsvColumn[],
    filterParams?: TableFilterRequest,
  ): string {
    return `${apiBaseUrl}/auth/plant/${plantId}/observations/${componentId}/${dFrom}/${dTo}${
      this.getQueryParams(params)}&csv=1&csv_columns=${encodeURIComponent(JSON.stringify(csvColumns))}${
      this.getEncodedAnalysisResultFilterParams(filterParams)}`;
  }

  public async getObservationsByDtIds(
    plantId: string,
    componentId: number,
    dFrom: string,
    dTo: string,
    params: TableRequest,
    observationFilter: ObservationFilterRequest,
    filterParams?: TableFilterRequest,
  ): Promise<TableResultSchema<ObservationSchema, ObservationColumn>> {
    return await this.post(`/auth/plant/${plantId}/observations/${componentId}/${dFrom}/${dTo}${
      this.getQueryParams(params)}${this.getEncodedAnalysisResultFilterParams(filterParams)}`,
      observationFilter
    );
  }

  public async createOrUpdateObservationForComponents(
    plantId: string,
    componentId: number,
    observationComponentsRequest: ObservationComponentsRequest,
  ): Promise<void> {
    await this.post(`/auth/plant/${plantId}/observation-for-components/${componentId}`, observationComponentsRequest);
  }

  public async deleteObservationForComponents(
    plantId: string,
    componentId: number,
    deleteObservationComponentsRequest: DeleteObservationComponentsRequest,
  ): Promise<void> {
    await this.post(`/auth/plant/${plantId}/delete-observation-for-components/${componentId}`, deleteObservationComponentsRequest);
  }

  //
  // Drone
  //

  public async getDronePlantCoverage(analysisId: string): Promise<void> {
    return this.get(`/auth/analysis/${analysisId}/drone-plant-coverage`);
  }

  public async getDroneModels(): Promise<DetailedDroneModelSchema[]> {
    return this.get(`/auth/drone-models`);
  }

  public async getDroneModel(droneModelId: string): Promise<DetailedDroneModelSchema> {
    return this.get(`/auth/drone-model/${droneModelId}`);
  }

  public async createDroneModel(
    createDroneModelRequest: CreateDroneModelRequest,
  ): Promise<void> {
    return this.post(`/auth/create-drone-model`, createDroneModelRequest)
  }

  public async updateDroneModel(
    droneModelId: string,
    updateDroneModelRequest: UpdateDroneModelRequest,
  ): Promise<void> {
    return this.post(`/auth/drone-model/${droneModelId}`, updateDroneModelRequest)
  }

  public async deleteDroneModel(droneModelId: string): Promise<void> {
    await this.delete(`/auth/drone-model/${droneModelId}`);
  }

  public async getDrones(dronesRequest: DronesRequest): Promise<DroneSchema[]> {
    return this.get(`/auth/drones`, dronesRequest);
  }

  public async getDronesSimple(dronesRequest: DronesRequest): Promise<SimpleDroneSchema[]> {
    return this.get(`/auth/drones-simple`, dronesRequest);
  }

  public async getGenericDrones(): Promise<DroneSchema[]> {
    return this.get(`/auth/generic-drones`);
  }

  public async getGenericDronesSimple(): Promise<SimpleDroneSchema[]> {
    return this.get(`/auth/generic-drones-simple`);
  }

  public async createDrone(
    createDroneRequest: CreateDroneRequest,
  ): Promise<void> {
    return this.post(`/auth/create-drone`, createDroneRequest)
  }

  public async updateDrone(
    droneId: string,
    updateDroneRequest: UpdateDroneRequest,
  ): Promise<void> {
    return this.post(`/auth/drone/${droneId}`, updateDroneRequest)
  }

  public async deleteDrone(droneId: string): Promise<void> {
    await this.delete(`/auth/drone/${droneId}`);
  }

  public async getAvailableDronesForAnalysis(analysisId: string): Promise<SimpleDroneSchema[]> {
    return this.get(`/auth/drones/${analysisId}`);
  }

  //
  // Evaluation Batches
  //

  public async getEvaluationBatchesCurrentFlightCampaign(analysisId: string, evalBatchFilter: EvalBatchFilterRequest): Promise<EvaluationBatchSchema[]> {
    return this.get(`/auth/analysis/${analysisId}/evaluation-batches${this.getQueryParams(evalBatchFilter)}`);
  }

  public async getEvaluationBatchesPlant(plantId: string): Promise<EvaluationBatchSchema[]> {
    return this.get(`/auth/plant/${plantId}/evaluation-batches`);
  }

  public async updateFlightRouteEvalBatch(flightRouteId: string, updateFlightRouteEvalBatchRequest: UpdateFlightRouteEvalBatchRequest): Promise<EvaluationBatchSchema> {
    return this.post(`/auth/flight-route/${flightRouteId}/update-flight-route-eval-batch`, updateFlightRouteEvalBatchRequest)
  }

  public async updateFlightRouteStartTime(flightRouteId: string, updateFlightRouteStartTimeRequest: UpdateFlightRouteStartTimeRequest): Promise<FlightRouteSchema> {
    return this.post(`/auth/flight-route/${flightRouteId}/update-start-time`, updateFlightRouteStartTimeRequest)
  }

  public async moveFlightRoute(flightRouteId: string, moveFlightRouteRequest: MoveFlightRouteRequest): Promise<FlightRouteSchema> {
    return this.post(`/auth/flight-route/${flightRouteId}/move`, moveFlightRouteRequest)
  }

  //
  // Flight Campaign and Flight Route
  //

  public async getFlightCampaignsOfAnalysis(analysisId: string): Promise<FlightCampaignSchema[]> {
    return this.get(`/auth/analysis/${analysisId}/flight-campaigns`);
  }

  public async getAnalysisFileFlightCampaigns(analysisId: string): Promise<AnalysisFileFlightCampaignsSchema> {
    return this.get(`/auth/analysis/${analysisId}/file-flight-campaigns`);
  }

  public async getCurrentFlightCampaignOfAnalysis(analysisId: string): Promise<FlightCampaignSchema | null> {
    return this.get(`/auth/analysis/${analysisId}/current-flight-campaign`);
  }

  public async getFlightRoutesOfFlightCampaign(flightCampaignId: string): Promise<FlightRouteSchema[]> {
    return this.get(`/auth/flight-campaign/${flightCampaignId}/flight-routes`);
  }

  public async getPlantActionsOfFlightCampaign(flightCampaignId: string): Promise<PlantOperationActionSchema[]> {
    return this.get(`/auth/flight-campaign/${flightCampaignId}/plant-actions`);
  }

  public getFlightRouteJsonUrl(flightRouteId: string): string {
    return `${apiBaseUrl}/auth/flight-route/${flightRouteId}/mission-json-file`;
  }

  public getFlightRouteDjiWpmlUrl(flightRouteId: string): string {
    return `${apiBaseUrl}/auth/flight-route/${flightRouteId}/dji-wpml-file`;
  }

  public getFlightRouteUnleashUrl(flightRouteId: string): string {
    return `${apiBaseUrl}/auth/flight-route/${flightRouteId}/unleash-json-file`;
  }

  public async exportFlightCampaignToUnleash(flightCampaignId: string): Promise<void> {
    return this.post(`/auth/flight-campaign/${flightCampaignId}/export-to-unleash`);
  }

  /** IANA time zone id for coordinates (tzfpy on the server). */
  public async getIanaTimeZoneByGps(lat: number, lon: number): Promise<string | null> {
    const raw = await this.get(`/auth/timezone/zone-by-gps`, { lat, long: lon });
    if (raw == null || raw === "") {
      return null;
    }
    return String(raw);
  }

  public async getFlighthub2Organizations(): Promise<Flighthub2OrganizationSchema[]> {
    return this.get(`/auth/fh2/organizations`);
  }

  public async getFlighthub2Projects(fh2_organization_uuid: string): Promise<Flighthub2ProjectSchema[]> {
    return this.get(`/auth/fh2/projects`, { fh2_organization_uuid });
  }

  public async getFlighthub2FlightRoutesInProject(fh2_project_uuid: string, fh2_organization_uuid: string): Promise<Flighthub2FlightRouteSchema[]> {
    return this.get(`/auth/fh2/${fh2_project_uuid}/flight-routes`, { fh2_organization_uuid });
  }

  public async getNumberOfDocksInFlighthub2Project(fh2_project_uuid: string, fh2_organization_uuid: string): Promise<number> {
    const docks = await this.getDocksInFlighthub2Project(fh2_project_uuid, fh2_organization_uuid);
    return docks.length;
  }

  public async getDocksInFlighthub2Project(
    fh2_project_uuid: string,
    fh2_organization_uuid: string,
  ): Promise<Flighthub2DockSummarySchema[]> {
    return this.get(`/auth/fh2/${fh2_project_uuid}/docks`, { fh2_organization_uuid });
  }

  public async getUpcomingTasksInFlighthub2Project(
    fh2_project_uuid: string,
    fh2_organization_uuid: string,
    dock_serial_number: string | null | undefined,
    begin_at: number,
    end_at: number,
  ): Promise<Flighthub2TaskPlanSchema[]> {
    return this.get(`/auth/fh2/${fh2_project_uuid}/upcoming-tasks-in-project`, {
      fh2_organization_uuid,
      ...(dock_serial_number ? { dock_serial_number } : {}),
      begin_at,
      end_at,
    });
  }

  public async getFlighthub2DockDeviceInfo(
    fh2_project_uuid: string,
    fh2_organization_uuid: string,
    dock_serial_number?: string | null,
  ): Promise<Flighthub2DockDeviceInfoSchema | null> {
    const dockDeviceInfo = await this.get(`/auth/fh2/${fh2_project_uuid}/dock-device-info`, {
      fh2_organization_uuid,
      ...(dock_serial_number ? { dock_serial_number } : {}),
    });
    if (!dockDeviceInfo?.dock_serial_number) {
      return null;
    }
    return dockDeviceInfo;
  }

  public async startFlighthub2LiveStream(
    fh2_project_uuid: string,
    startFlighthub2LiveStreamRequest: StartFlighthub2LiveStreamRequest,
  ): Promise<Flighthub2LiveStreamSchema> {
    return this.post(`/auth/fh2/${fh2_project_uuid}/live-stream/start`, startFlighthub2LiveStreamRequest);
  }

  public async updateFlighthub2LiveStreamJoinState(
    fh2_project_uuid: string,
    updateFlighthub2LiveStreamJoinStateRequest: UpdateFlighthub2LiveStreamJoinStateRequest,
  ): Promise<void> {
    await this.post(
      `/auth/fh2/${fh2_project_uuid}/live-stream/join-state`,
      updateFlighthub2LiveStreamJoinStateRequest,
    );
  }

  // Slow endpoint (budget 30 s): trigger it from an <app-button long-loading-time>
  public async exportFlightCampaignToFlighthub2(exportFlightCampaignFlighthub2Request: ExportFlightCampaignFlighthub2Request): Promise<void> {
    return this.post(`/auth/fh2/export-flight-campaign`, exportFlightCampaignFlighthub2Request);
  }

  public async deleteAllFlightRoutesInFlighthub2Project(fh2_project_uuid: string, fh2_organization_uuid: string): Promise<void> {
    return this.delete(`/auth/fh2/${fh2_project_uuid}/delete-all-flight-routes-in-project?fh2_organization_uuid=${encodeURIComponent(fh2_organization_uuid)}`);
  }

  public async deleteFlighthub2FlightRouteInProject(fh2_project_uuid: string, flight_route_id: string): Promise<void> {
    await this.delete(`/auth/fh2/${fh2_project_uuid}/flight-routes/${flight_route_id}`);
  }

  public async suspendFlighthub2TaskPlan(fh2_project_uuid: string, task_plan_id: string | number): Promise<void> {
    await this.put(`/auth/fh2/${fh2_project_uuid}/task-plans/${task_plan_id}/suspend`);
  }

  public async resumeFlighthub2TaskPlan(fh2_project_uuid: string, task_plan_id: string | number): Promise<void> {
    await this.put(`/auth/fh2/${fh2_project_uuid}/task-plans/${task_plan_id}/resume`);
  }

  public async deleteFlighthub2TaskPlan(fh2_project_uuid: string, task_plan_id: string | number): Promise<void> {
    await this.delete(`/auth/fh2/${fh2_project_uuid}/task-plans/${task_plan_id}`);
  }

  public async getMediaFoldersFlighthub2(fh2_project_uuid: string, fh2_organization_uuid: string, parent_folder_id?: string): Promise<Flighthub2FolderSchema[]> {
    return this.get(`/auth/fh2/${fh2_project_uuid}/media-folders`, {
      fh2_organization_uuid,
      parent_folder_id: parent_folder_id ? parent_folder_id : '',
    });
  }

  public async importFilesFromFlighthub2(importFilesFromFlighthub2Request: ImportFilesFromFlighthub2Request): Promise<TaskSchema> {
    return this.post(`/auth/fh2/download-media-files`, importFilesFromFlighthub2Request);
  }

  public async getMediaFoldersUnleash(): Promise<UnleashFolderSchema[]> {
    return this.get(`/auth/unleash-folders`);
  }

  public async importFilesFromUnleash(importFilesFromUnleashRequest: ImportFilesFromUnleashRequest): Promise<void> {
    return this.post(`/auth/download-unleash-files`, importFilesFromUnleashRequest);
  }

  // Slow endpoint (budget 45 s): trigger it from an <app-button long-loading-time>
  public async createFlightCampaign(
    createFlightCampaignRequest: CreateFlightCampaignRequest,
  ): Promise<void> {
    return this.post(`/auth/flight-campaign`, createFlightCampaignRequest)
  }

  public async updateFlightCampaign(flightCampaignId: string, updateFlightCampaignRequest: UpdateFlightCampaignRequest): Promise<void> {
    await this.post(`/auth/flight-campaign/${flightCampaignId}/update`, updateFlightCampaignRequest);
  }

  public async regenerateFlightCampaign(flightCampaignId: string): Promise<void> {
    await this.post(`/auth/flight-campaign/${flightCampaignId}/regenerate`);
  }

  public async duplicateFlightCampaign(
    flightCampaignId: string,
    duplicateFlightCampaignRequest: DuplicateFlightCampaignRequest,
  ): Promise<void> {
    await this.post(`/auth/flight-campaign/${flightCampaignId}/duplicate`, duplicateFlightCampaignRequest);
  }

  /** Calculates all PV flight routes of the campaign synchronously on the server
   * (meander routes, NOFLY avoidance, sun time windows, start times, image estimates). */
  public async calculatePvFlightRoutes(flightCampaignId: string): Promise<PvFlightRouteCalculationResultSchema> {
    return this.post(`/auth/flight-campaign/${flightCampaignId}/calculate-pv-flight-routes`);
  }

  /** Moves a PV flight campaign to a new date: sun time windows and route start times
   * are recalculated while the route geometry stays unchanged. */
  public async reschedulePvFlightCampaign(
    flightCampaignId: string,
    reschedulePvFlightCampaignRequest: ReschedulePvFlightCampaignRequest,
  ): Promise<PvFlightRouteCalculationResultSchema> {
    return this.post(`/auth/flight-campaign/${flightCampaignId}/reschedule`, reschedulePvFlightCampaignRequest);
  }

  /** Edits a PV flight campaign with the full set of (prefilled) values. The server
   * applies the cheapest sufficient action: full recalculation, recording-speed update,
   * sun/start-time reschedule, or a plain field update (see applied_action).
   * Slow endpoint (budget 45 s): trigger it from an <app-button long-loading-time> */
  public async editPvFlightCampaign(
    flightCampaignId: string,
    editPvFlightCampaignRequest: EditPvFlightCampaignRequest,
  ): Promise<PvEditFlightCampaignResultSchema> {
    return this.post(`/auth/flight-campaign/${flightCampaignId}/edit-pv`, editPvFlightCampaignRequest);
  }

  public async deleteFlightCampaign(flightCampaignId: string): Promise<void> {
    await this.delete(`/auth/flight-campaign/${flightCampaignId}`);
  }

  public async getFlightRouteMissionJson(flightRouteId: string): Promise<FlightRouteMissionSchema> {
    return this.get(`/auth/flight-route/${flightRouteId}/mission`);
  }

  public async deleteFlightRoute(flightRouteId: string): Promise<void> {
    await this.delete(`/auth/flight-route/${flightRouteId}`);
  }

  public async deletePlantOperationAction(flightCampaignId: string, plantOperationActionId: string): Promise<void> {
    await this.delete(`/auth/flight-campaign/${flightCampaignId}/delete-plant-operation-action/${plantOperationActionId}`);
  }

  public async updatePlantOperationActionStartTime(
    flightCampaignId: string,
    plantOperationActionId: string,
    updatePlantOperationActionStartTimeRequest: UpdatePlantOperationActionStartTimeRequest,
  ): Promise<PlantOperationActionSchema> {
    return this.post(
      `/auth/flight-campaign/${flightCampaignId}/plant-operation-action/${plantOperationActionId}/update-start-time`,
      updatePlantOperationActionStartTimeRequest,
    );
  }

  public async getCurrentFlightRoutes(analysisId: string): Promise<FlightRouteSchema[]> {
    return this.get(`/auth/analysis/${analysisId}/current-flight-routes`);
  }

  // 
  // Product Package and Order 
  //

  public async createProductPackage(
    createProductPackageRequest: CreateProductPackageRequest,
  ): Promise<void> {
    return this.post(`/auth/create-product-package`, createProductPackageRequest)
  }

  public async updateProductPackage(
    productPackageId: number,
    updateProductPackageRequest: UpdateProductPackageRequest,
  ): Promise<void> {
    return this.post(`/auth/product-package/${productPackageId}`, updateProductPackageRequest)
  }

  public async deleteProductPackage(productPackageId: number): Promise<void> {
    await this.delete(`/auth/product-package/${productPackageId}`);
  }

  public async getProductPackagesWithKeyFigures(
    productPackagesWithKeyFiguresRequest?: ProductPackagesWithKeyFiguresRequest,
  ): Promise<ProductPackageWithKeyFiguresSchema[]> {
    if (productPackagesWithKeyFiguresRequest?.technology_ids === undefined) {
      productPackagesWithKeyFiguresRequest = { technology_ids: [] };
    }
    return this.post(`/auth/product-packages-with-key-figures`, productPackagesWithKeyFiguresRequest);
  }

  public async getProductPackagesNames(technologyId?: number): Promise<ProductPackageSchema[]> {
    return this.get(`/auth/product-packages-names`, technologyId ? { technology_id: technologyId } : undefined);
  }

  public async getPvFlightParams(): Promise<PvFlightParamsSchema[]> {
    return this.get(`/auth/pv-flight-params`);
  }

  public async updatePvFlightParams(updatePvFlightParamsRequest: UpdatePvFlightParamsRequest): Promise<void> {
    return this.post(`/auth/pv-flight-params`, updatePvFlightParamsRequest);
  }

  public async createOrder(
    createOrderRequest: CreateOrderRequest,
  ): Promise<void> {
    return this.post(`/auth/order`, createOrderRequest);
  }

  public async updateOrder(
    orderId: string,
    updateOrderRequest: UpdateOrderRequest,
  ): Promise<void> {
    return this.post(`/auth/order/${orderId}`, updateOrderRequest);
  }

  public async getOrdersForTable(
    queryParams: { plant_id?: string, customer_id?: string } & TableRequest & { limit: number },
  ): Promise<TableResultSchema<OrderWithZohoSchema>> {
    return this.get(`/auth/orders`, queryParams);
  }

  public async deleteOrder(orderId: string): Promise<void> {
    return this.delete(`/auth/order/${orderId}`);
  }

  public async getOrderProductPackages(plantId: string, curDate?: string, customerId?: string): Promise<OrderProductPackageSchema[]> {
    const queryParams = {};
    if (curDate) {
      queryParams["cur_date"] = curDate;
    }
    if (customerId) {
      queryParams["customer_id"] = customerId;
    }

    return this.get(`/auth/order-product-packages/${plantId}`, queryParams);
  }

  private getOrderPPDisplayNumber(
    orderPP: OrderProductPackageSchema,
    isNewAnalysis: boolean,
    analysisOppMap: Map<string, OrderProductPackageSchema> | null,
    plantCapacityMw?: number,
    walletHasSoiling = false,
  ): string | undefined {
    if (orderPP.booked_mw != null) {
      return this.getMwDisplayNumber(orderPP, isNewAnalysis, analysisOppMap, plantCapacityMw);
    }
    if (orderPP.booked_credits != null) {
      const analysisOpp = isNewAnalysis ? undefined : analysisOppMap?.get(orderPP.id);
      return OrderHelper.usageLabel(
        analysisOpp
          ? { ...orderPP, analysis_credits_within_order_product_package: analysisOpp.analysis_credits_within_order_product_package }
          : orderPP,
        { plantCapacityMw, isNewAnalysis, alreadyLinked: !!analysisOpp, hasSoiling: walletHasSoiling },
      );
    }

    let displayNum: string | undefined;

    if (!isNewAnalysis && analysisOppMap?.has(orderPP.id)) {
      const analysisOpp = analysisOppMap.get(orderPP.id)!;
      const analysisPosition = analysisOpp.analysis_position_within_order_product_package;
      displayNum = analysisPosition != null
        ? analysisPosition + "/" + orderPP.booked_quantity
        : undefined;
    } else if (isNewAnalysis) {
      displayNum = (orderPP.total_analyses_count + 1) + "/" + orderPP.booked_quantity;
    } else {
      displayNum = (orderPP.total_analyses_count + 1) + "/" + orderPP.booked_quantity;
    }

    if (displayNum == null && orderPP.booked_quantity) {
      return "?/" + orderPP.booked_quantity;
    }
    return displayNum;
  }

  private getMwDisplayNumber(
    orderPP: OrderProductPackageSchema,
    isNewAnalysis: boolean,
    analysisOppMap: Map<string, OrderProductPackageSchema> | null,
    plantCapacityMw?: number,
  ): string {
    const bookedMw = orderPP.booked_mw!;

    const bookedMwDisplay = MathHelper.formatMw(bookedMw);

    if (!isNewAnalysis && analysisOppMap?.has(orderPP.id)) {
      const analysisOpp = analysisOppMap.get(orderPP.id)!;
      const analysisMw = analysisOpp.analysis_mw_within_order_product_package;
      if (analysisMw != null) {
        return MathHelper.formatMw(analysisMw) + "/" + bookedMwDisplay + " MW";
      }
      return "?/" + bookedMwDisplay + " MW";
    }

    const totalMw = orderPP.total_analyses_mw || 0;
    const previewMw = isNewAnalysis && plantCapacityMw ? totalMw + plantCapacityMw : totalMw;
    return MathHelper.formatMw(previewMw) + "/" + bookedMwDisplay + " MW";
  }

  public async getOrderPPsMultiselectOptions(
    isNewAnalysis = false,
    plantId: string,
    curDate?: string,
    customerId?: string,
    analysisOrderPPs?: OrderProductPackageSchema[],
    isZombieAnalysis?: boolean,
    plantCapacityMw?: number,
  ): Promise<{ options: OrderPPMultiselectOption[], orderPPs: OrderProductPackageSchema[] }> {
    // if this is requested for a new analysis: we take the current total number of analyses for this order product package and add 1 (to show the future value)
    // otherwise: we take the analysis number within the order product package (per-analysis when analysisOrderPPs provided)

    const orderPPs = await volateqApi.getOrderProductPackages(plantId, curDate, customerId);
    const analysisOppMap = analysisOrderPPs
      ? new Map(analysisOrderPPs.map(opp => [opp.id, opp]))
      : null;
    const walletHasSoiling = OrderHelper.walletHasSoiling(orderPPs);

    const options = orderPPs
      .map((orderPP): OrderPPMultiselectOption => {
        let displayNum: string | undefined;
        if (isZombieAnalysis === true && orderPP.booked_mw != null) {
          displayNum = "?/" + MathHelper.formatMw(orderPP.booked_mw) + " MW";
        } else if (isZombieAnalysis === true && orderPP.booked_credits != null) {
          displayNum = OrderHelper.formatCredits(null, orderPP.booked_credits);
        } else if (isZombieAnalysis === true && orderPP.booked_quantity) {
          displayNum = "?/" + orderPP.booked_quantity;
        } else {
          displayNum = this.getOrderPPDisplayNumber(orderPP, isNewAnalysis, analysisOppMap, plantCapacityMw, walletHasSoiling);
        }
        const hasCounter = orderPP.booked_quantity > 0 || orderPP.booked_mw != null || orderPP.booked_credits != null;
        const usage = hasCounter ? displayNum : undefined;
        return {
          id: orderPP.id,
          label: usage ? orderPP.product_package.name + " (" + usage + ")" : orderPP.product_package.name,
          productPackageName: orderPP.product_package.name,
          usage,
          walletOrderId: OrderHelper.walletOrderIdOf(orderPP),
        };
      });

    return {
      options,
      orderPPs,
    };
  }

  public async getNewAnalysisProductPackagesProposal(plantId: string, curDate?: string): Promise<OrderProductPackageSchema[]> {
    return this.get(`/auth/new-analysis-product-packages-proposal/${plantId}`, curDate ? { cur_date: curDate } : undefined)
  }

  public async getOrderPPDisabledKeyFigures(orderId: string): Promise<OrderPPKeyFiguresDisabledSchema[]> {
    return this.get(`/auth/order-product-packages/${orderId}/disabled-key-figures`);
  }

  //
  // Upload
  //

  public async getSecuredFilenames(filenames: string[]): Promise<SecuredFilename[]> {
    return this.post(`/auth/upload/secure-filenames`, { file_names: filenames });
  }

  public async createAnalysisTmpUpload(createUploadTmpRequest: CreateAnalysisTmpUploadRequest): Promise<Upload> {
    return this.post(`/auth/upload/analysis-tmp`, createUploadTmpRequest);
  }

  public async createAnalysisUpload(createUploadRequest: CreateAnalysisUploadRequest): Promise<Upload> {
    return this.post(`/auth/upload/analysis`, createUploadRequest);
  }

  public async generateUploadChunkUrl(uploadId: string, uploadChunkUrlRequest: CreateUploadPartUrlRequest): Promise<UploadFileChunkSchema> {
    return this.post(`/auth/upload/${uploadId}/next-chunk`, uploadChunkUrlRequest);
  }

  // Slow endpoint (budget 180 s)
  public async finishFileUpload(uploadId: string, uploadChunkUrlRequest: FinishUploadRequest): Promise<void> {
    return this.post(`/auth/upload/${uploadId}/finish`, uploadChunkUrlRequest);
  }

  public async startUploadFiles(uploadId: string, startUploadFilesRequest: StartUploadFilesRequest): Promise<UploadFileChunkSchema[]> {
    return this.post(`/auth/upload/${uploadId}/start-files`, startUploadFilesRequest);
  }

  public async completeUploadFiles(uploadId: string, completeUploadFilesRequest: CompleteUploadFilesRequest): Promise<CompleteUploadFilesSchema> {
    return this.post(`/auth/upload/${uploadId}/complete-files`, completeUploadFilesRequest);
  }

  public async cancelUpload(uploadId: string): Promise<void> {
    await this.post(`/auth/upload/${uploadId}/cancel`);
  }

  //
  // Report
  //

  public async createReport(plantId: string, plantImageFile: File, createReportRequest: CreateReportRequest): Promise<TaskSchema> {
    const formRecord: Record<string, string | File> = { "plant_image_file": plantImageFile };
    for (const k of Object.keys(createReportRequest)) {
      if (createReportRequest[k] !== null) {
        formRecord[k] = Array.isArray(createReportRequest[k]) ? JSON.stringify(createReportRequest[k]) : createReportRequest[k].toString();
      }
    }

    return await this.postForm(`/auth/plant/${plantId}/report`, formRecord);
  }

  public async estimateReportSize(
    plantId: string,
    createReportRequest: CreateReportRequest,
    signal?: AbortSignal,
  ): Promise<{ page_count: number }> {
    const formRecord: Record<string, string | File> = {};
    for (const k of Object.keys(createReportRequest)) {
      if (createReportRequest[k] !== null) {
        formRecord[k] = Array.isArray(createReportRequest[k]) ? JSON.stringify(createReportRequest[k]) : createReportRequest[k].toString();
      }
    }

    return await this.postForm(`/auth/plant/${plantId}/report/estimate-size`, formRecord, undefined, { signal });
  }

  public async getReportModuleAnomalyCounts(
    plantId: string,
    analysisResultId: string,
    onlyReleasedEvalBatches: boolean,
    evalBatchNums: number[] | null,
  ): Promise<ReportModuleAnomalyCountsSchema> {
    return this.get(`/auth/plant/${plantId}/report/module-anomaly-counts`, {
      analysis_result_id: analysisResultId,
      only_released_eval_batches: onlyReleasedEvalBatches,
      ...EvalBatchSelectionHelper.toQueryParam(evalBatchNums),
    });
  }

  public async getReports(plantId: string): Promise<ReportSchema[]> {
    return this.get(`/auth/plant/${plantId}/reports`);
  }

  public async getReportUrl(plantId: string, reportId: string): Promise<{ url: string }> {
    return this.get(`/auth/plant/${plantId}/report/${reportId}`);
  }

  public async deleteReport(plantId: string, reportId: string) {
    await this.delete(`/auth/plant/${plantId}/report/${reportId}`);
  }

  //
  // Dashboard
  //

  /**
   * `widgetNames` limits the response to those widgets. Widget data can be expensive to
   * compute (external APIs such as Open-Meteo), so only ask for the widgets that are
   * actually rendered.
   *
   * `includePerArea: false` leaves the per-region breakdown out of the widgets that have one.
   * It is the slowest part of a dashboard load, so the dashboard fetches everything else first
   * and asks for the regions in a second request. Those widgets' `per_area` is null then.
   */
  public async getDashboard(options?: {
    plantId?: string;
    plantIds?: string[];
    widgetNames?: string[];
    flightTimelineStartDate?: string;
    flightTimelineIncludeAnalysisId?: string;
    includePerArea?: boolean;
    userSessionsIncludeYearly?: boolean;
    evaluatedCapacityIncludePositions?: boolean;
    currentAnalysisIncludeDataChecks?: boolean;
  }): Promise<DashboardSchema> {
    if (options?.plantIds?.length) {
      return await this.get(`/auth/dashboard`, { plant_ids: options.plantIds.join(",") });
    }

    const params: Record<string, string> = {};
    if (options?.plantId) {
      params.plant_id = options.plantId;
    }
    if (options?.widgetNames?.length) {
      params.widget_names = options.widgetNames.join(",");
    }
    if (options?.flightTimelineStartDate) {
      params.flight_timeline_start_date = options.flightTimelineStartDate;
    }
    if (options?.flightTimelineIncludeAnalysisId) {
      params.flight_timeline_include_analysis_id = options.flightTimelineIncludeAnalysisId;
    }
    if (options?.includePerArea === false) {
      params.include_per_area = "false";
    }
    if (options?.userSessionsIncludeYearly) {
      params.user_sessions_include_yearly = "true";
    }
    if (options?.evaluatedCapacityIncludePositions) {
      params.evaluated_capacity_include_positions = "true";
    }
    if (options?.currentAnalysisIncludeDataChecks) {
      params.current_analysis_include_data_checks = "true";
    }

    return await this.get(`/auth/dashboard`, Object.keys(params).length > 0 ? params : undefined);
  }

  public async getWidgets(): Promise<WidgetWithParamsSchema[]> {
    return await this.get(`/auth/dashboard/widgets`);
  }

  public async updateWidget(dashboardWidgetId: string, updateWidgetRequest: UpdateWidgetRequest): Promise<void> {
    await this.post(`/auth/dashboard/widget/${dashboardWidgetId}`, updateWidgetRequest);
  }

  public async selectWidgets(selectedWidgetIds: string[]): Promise<void> {
    await this.post(`/auth/dashboard/widgets/select`, { selected_widget_ids: selectedWidgetIds });
  }

  /**
   * Compare mode of the executive summary widget: the released analyses of the plant and the comparison of the
   * executive summary's analysis with the one before it. piIds is the PI selection of the widget.
   */
  public async getCompareAnalysisData(plantId: string, piIds: string[]): Promise<CompareAnalysisData> {
    return await this.get(`/auth/dashboard/compare-analysis/${plantId}`, { pi_ids: piIds.join(",") });
  }

  /**
   * Comparison of two released analyses for the compare mode of the executive summary widget. The newer one is
   * returned as "analysis". piIds is the PI selection of the widget.
   */
  public async getCompareAnalysis(
    plantId: string,
    analysisId: string,
    compareAnalysisId: string,
    piIds: string[],
  ): Promise<CompareAnalysisComparison> {
    return await this.get(`/auth/dashboard/compare-analysis/${plantId}/${analysisId}/${compareAnalysisId}`, {
      pi_ids: piIds.join(","),
    });
  }

  /** Affected modules of a PI in every released analysis of the plant that has the PI, oldest first. */
  public async getCompareAnalysisPiHistory(plantId: string, piId: number): Promise<CompareAnalysisPiHistoryEntry[]> {
    return await this.get(`/auth/dashboard/compare-analysis-pi-history/${plantId}/${piId}`);
  }

}

export const volateqApi: VolateqAPI = new VolateqAPI();
